import { useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Title,
  Text,
  Card,
  Grid,
  Badge,
  Flex,
  ProgressBar,
  Select,
  SelectItem,
  AreaChart,
  DonutChart,
  BarChart,
  Button,
  Metric,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Callout,
  Accordion,
  AccordionHeader,
  AccordionBody,
  NumberInput,
} from '@tremor/react'
import {
  ArrowLeftIcon,
  CpuChipIcon,
  WifiIcon,
  SpeakerWaveIcon,
  MicrophoneIcon,
  BoltIcon,
  ClockIcon,
  MapPinIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  ArrowPathIcon,
  SignalIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  Cog6ToothIcon,
  Battery100Icon,
  PhoneIcon,
  TrashIcon,
  KeyIcon,
  ChatBubbleLeftRightIcon,
  LinkIcon,
} from '@heroicons/react/24/outline'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getMachinesWithMetrics, getMachineMetricsHistory, getNetworkHistory, getAudioHistory, getBatteryHistory, getAudioMetricsHistory, getAudioDevicesHistory } from '@/api/machines'
import { executeCommand, getCommandHistory, installTailscale, uninstallTailscale, type CommandType } from '@/api/commands'
import { listDiagnostics, downloadDiagnosticFile, exportDiagnostics, type DiagnosticSummary } from '@/api/diagnostics'
import { getOperatorByMachineId, updateOperator } from '@/api/operators'
import { getActivitySummary, getActivityTimeline, getFocusCorrelation, type ActivityPeriod, type FocusCorrelationWindow } from '@/api/activity'
import { AudioConfigModal } from '@/components/AudioConfigModal'
import { CallTimeline } from '@/components/CallTimeline'
import { PhoneIndicator } from '@/components/VoipStatusBadge'
import { useWebSocket } from '@/context/WebSocketContext'
import type { MachineWithMetrics, MetricsHistory, NetworkHistoryEntry, AudioHistoryEntry, AudioMetricsHistory, AudioDeviceChange, BatteryHistoryEntry } from '@/types'
import type { Command } from '@/api/commands'
import { getMachineStatusColor, getMachineStatusLabel } from '@/utils/machineStatus'
import { getQualityVisual } from '@/utils/networkQuality'
import { getEmcallStatus, emcallDriftLabel } from '@/utils/audioEmcall'
import { usePermissions } from '@/hooks/usePermissions'

type Period = '4h' | '24h' | '7d' | '30d'
const ACTIVITY_PERIOD_HOURS: Record<ActivityPeriod, number> = { '24h': 24, '7d': 168, '30d': 720 }

// FastAPI 422 returns `detail` as an array of {loc, msg, type, ...} objects.
// Rendering that directly as a React child triggers Minified React error #31.
function formatErrorDetail(detail: unknown, fallback: string): string {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    return detail
      .map((d: any) => (typeof d === 'string' ? d : d?.msg || JSON.stringify(d)))
      .join(' ; ')
  }
  if (detail && typeof detail === 'object') {
    return (detail as any).msg || JSON.stringify(detail)
  }
  return fallback
}

// Types for major events
interface MajorEvent {
  time: string
  type: 'network_change' | 'quality_degradation' | 'quality_improvement' | 'audio_issue' | 'connection_lost' | 'speedtest' | 'power'
  severity: 'info' | 'warning' | 'error' | 'success'
  title: string
  description: string
  details?: Record<string, unknown>
}

// jsonb columns come back as JSON strings through the commands endpoint —
// parse once so callers can read fields directly.
function parseCmdParams(cmd: Command): Record<string, unknown> | null {
  const p = cmd.parameters as Record<string, unknown> | string | null
  if (typeof p === 'string') {
    try {
      return JSON.parse(p) as Record<string, unknown>
    } catch {
      return null
    }
  }
  return p
}

function getProgressColor(value: number, thresholds: { warning: number; danger: number }): 'green' | 'yellow' | 'red' {
  if (value >= thresholds.danger) return 'red'
  if (value >= thresholds.warning) return 'yellow'
  return 'green'
}

function formatDate(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatTimeForPeriod(dateString: string, period: string): string {
  const date = new Date(dateString)
  if (period === '4h' || period === '24h') {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  } else if (period === '7d') {
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  } else {
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
  }
}

function formatDateTime(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Extract major events from history data
function extractMajorEvents(
  networkHistory: NetworkHistoryEntry[],
  audioHistory: AudioHistoryEntry[],
  batteryHistory: BatteryHistoryEntry[] = [],
  commands: Command[] = []
): MajorEvent[] {
  const events: MajorEvent[] = []

  // Analyze network history for major changes
  for (let i = 0; i < networkHistory.length; i++) {
    const current = networkHistory[i]
    const previous = networkHistory[i + 1] // History is in reverse order

    // Network/SSID change
    if (previous && current.wifi_ssid !== previous.wifi_ssid) {
      events.push({
        time: current.start,
        type: 'network_change',
        severity: 'info',
        title: 'Changement de réseau WiFi',
        description: `${previous.wifi_ssid || 'Inconnu'} → ${current.wifi_ssid || 'Inconnu'}`,
        details: { from: previous.wifi_ssid, to: current.wifi_ssid }
      })
    }

    // Signal quality degradation (drop > 20%)
    if (previous && current.wifi_signal !== null && previous.wifi_signal !== null) {
      const signalDrop = previous.wifi_signal - current.wifi_signal
      if (signalDrop >= 20) {
        events.push({
          time: current.start,
          type: 'quality_degradation',
          severity: 'warning',
          title: 'Dégradation du signal WiFi',
          description: `Signal passé de ${previous.wifi_signal}% à ${current.wifi_signal}% (-${signalDrop}%)`,
          details: { from: previous.wifi_signal, to: current.wifi_signal, drop: signalDrop }
        })
      } else if (signalDrop <= -20) {
        events.push({
          time: current.start,
          type: 'quality_improvement',
          severity: 'success',
          title: 'Amélioration du signal WiFi',
          description: `Signal passé de ${previous.wifi_signal}% à ${current.wifi_signal}% (+${-signalDrop}%)`,
          details: { from: previous.wifi_signal, to: current.wifi_signal, improvement: -signalDrop }
        })
      }
    }

    // High ping detection
    if (current.ping_ms !== null && current.ping_ms > 100 &&
        (!previous || previous.ping_ms === null || previous.ping_ms <= 100)) {
      events.push({
        time: current.start,
        type: 'quality_degradation',
        severity: 'warning',
        title: 'Latence élevée détectée',
        description: `Ping à ${Math.round(current.ping_ms)} ms`,
        details: { ping: current.ping_ms }
      })
    }

    // High jitter detection
    if (current.jitter_ms !== null && current.jitter_ms > 50 &&
        (!previous || previous.jitter_ms === null || previous.jitter_ms <= 50)) {
      events.push({
        time: current.start,
        type: 'quality_degradation',
        severity: 'error',
        title: 'Jitter critique',
        description: `Jitter à ${Math.round(current.jitter_ms)} ms - Qualité VoIP dégradée`,
        details: { jitter: current.jitter_ms }
      })
    }

    // Speedtest results — only show when values change (agent caches and
    // resends the same result every 10s until the next real speedtest)
    if (current.download_mbps !== null && current.upload_mbps !== null &&
        (!previous || previous.download_mbps === null || previous.upload_mbps === null ||
         Math.round(current.download_mbps) !== Math.round(previous.download_mbps) ||
         Math.round(current.upload_mbps) !== Math.round(previous.upload_mbps))) {
      events.push({
        time: current.start,
        type: 'speedtest',
        severity: 'info',
        title: 'Test de débit effectué',
        description: `↓ ${Math.round(current.download_mbps)} Mbps  ↑ ${Math.round(current.upload_mbps)} Mbps`,
        details: { download: current.download_mbps, upload: current.upload_mbps }
      })
    }
  }

  // Analyze audio history for issues
  for (let i = 0; i < audioHistory.length; i++) {
    const current = audioHistory[i]
    const previous = audioHistory[i + 1]

    // Mic muted
    if (current.input_muted && (!previous || !previous.input_muted)) {
      events.push({
        time: current.start,
        type: 'audio_issue',
        severity: 'warning',
        title: 'Microphone mis en sourdine',
        description: 'Le microphone a été désactivé',
      })
    }

    // Mic unmuted
    if (!current.input_muted && previous?.input_muted) {
      events.push({
        time: current.start,
        type: 'audio_issue',
        severity: 'success',
        title: 'Microphone réactivé',
        description: 'Le microphone a été réactivé',
      })
    }

    // Low mic volume
    if (current.input_volume !== null && current.input_volume < 30 && !current.input_muted &&
        (!previous || previous.input_volume === null || previous.input_volume >= 30)) {
      events.push({
        time: current.start,
        type: 'audio_issue',
        severity: 'warning',
        title: 'Volume micro bas',
        description: `Volume du microphone à ${current.input_volume}%`,
      })
    }

    // Input device changed (headset tracking)
    if (previous && current.input_device && previous.input_device &&
        current.input_device !== previous.input_device) {
      events.push({
        time: current.start,
        type: 'audio_issue',
        severity: 'info',
        title: 'Changement de micro',
        description: `${previous.input_device} → ${current.input_device}`,
        details: { from: previous.input_device, to: current.input_device }
      })
    }

    // Output device changed (headset tracking)
    if (previous && current.output_device && previous.output_device &&
        current.output_device !== previous.output_device) {
      events.push({
        time: current.start,
        type: 'audio_issue',
        severity: 'info',
        title: 'Changement de sortie audio',
        description: `${previous.output_device} → ${current.output_device}`,
        details: { from: previous.output_device, to: current.output_device }
      })
    }
  }

  // Charger plugged / unplugged transitions. Each period after the
  // oldest is a state flip, so emit an event at its start.
  for (let i = 0; i < batteryHistory.length; i++) {
    const current = batteryHistory[i]
    const previous = batteryHistory[i + 1] // older period
    if (!previous) continue // oldest = data start, not a real transition
    events.push({
      time: current.start,
      type: 'power',
      severity: current.plugged ? 'success' : 'warning',
      title: current.plugged ? 'Chargeur branché' : 'Chargeur débranché',
      description: current.plugged
        ? 'Ordinateur rebranché sur le secteur'
        : 'Ordinateur débranché du secteur',
    })
  }

  // Add command results as events
  commands.forEach(cmd => {
    if (cmd.command_type === 'speedtest' && cmd.status === 'completed' && cmd.result) {
      let result
      try {
        result = typeof cmd.result === 'string' ? JSON.parse(cmd.result) : cmd.result
      } catch {
        return  // malformed speedtest result — skip rather than crash the page
      }
      const speedData = result.result || result
      if (speedData.download_mbps || speedData.upload_mbps) {
        events.push({
          time: cmd.created_at,
          type: 'speedtest',
          severity: 'info',
          title: 'Speedtest manuel',
          description: `↓ ${Number(speedData.download_mbps || 0).toFixed(1)} Mbps  ↑ ${Number(speedData.upload_mbps || 0).toFixed(1)} Mbps  ${Number(speedData.ping_ms || 0).toFixed(0)}ms`,
          details: speedData
        })
      }
    }
  })

  // Sort by time descending
  return events.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
}

function getEventIcon(type: MajorEvent['type']) {
  switch (type) {
    case 'network_change':
      return WifiIcon
    case 'quality_degradation':
      return ArrowTrendingDownIcon
    case 'quality_improvement':
      return ArrowTrendingUpIcon
    case 'audio_issue':
      return MicrophoneIcon
    case 'connection_lost':
      return XCircleIcon
    case 'speedtest':
      return SignalIcon
    case 'power':
      return BoltIcon
    default:
      return ExclamationTriangleIcon
  }
}

function getEventColor(severity: MajorEvent['severity']): 'blue' | 'yellow' | 'red' | 'green' {
  switch (severity) {
    case 'info':
      return 'blue'
    case 'warning':
      return 'yellow'
    case 'error':
      return 'red'
    case 'success':
      return 'green'
  }
}

// Every category the agent's _categorize_window_event can emit. On a fleet
// without the browser extension deployed, browser_unknown is a dominant one.
const ACTIVITY_CATEGORY_LABELS: Record<string, string> = {
  voip: 'En communication',
  browser_work: 'Navigateur — métier',
  browser_other: 'Navigateur — autre',
  browser_unknown: 'Navigateur — non identifié',
  afk: 'Inactif (AFK)',
  locked: 'Session verrouillée',
  other: 'Autres applications',
}

const ACTIVITY_CATEGORY_COLORS = ['blue', 'emerald', 'amber', 'orange', 'gray', 'slate', 'violet']

function activityCategoryLabel(category: string): string {
  return ACTIVITY_CATEGORY_LABELS[category] || category
}

function realtimeActivityLabel(afk: boolean | null, category: string | null): string | null {
  if (afk) return ACTIVITY_CATEGORY_LABELS.afk
  if (category) return ACTIVITY_CATEGORY_LABELS[category] || null
  return null
}

// Look up the focus-correlation window that contains a given event time,
// without issuing a new API call — omit silently if no window covers it.
function findCorrelationWindow(
  windows: FocusCorrelationWindow[] | undefined,
  eventTime: string
): FocusCorrelationWindow | null {
  if (!windows) return null
  const t = new Date(eventTime).getTime()
  return (
    windows.find((w) => {
      const start = new Date(w.window_start).getTime()
      const end = new Date(w.window_end).getTime()
      return t >= start && t <= end
    }) || null
  )
}

export function OperatorDetail() {
  // Viewers see metrics + history but no write actions / commands.
  const { canWrite, isAdmin, isManager } = usePermissions()
  const canViewActivity = isAdmin || isManager
  const { machineId } = useParams<{ machineId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [period, setPeriod] = useState<Period>('24h')
  const [mainTab, setMainTab] = useState(0)
  const [commandMessage, setCommandMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [showAudioConfig, setShowAudioConfig] = useState(false)
  const [showLinphoneModal, setShowLinphoneModal] = useState(false)
  const [linphonePassword, setLinphonePassword] = useState('')
  const [sipServer, setSipServer] = useState<'emcall.education-media.net' | 'emcall.emc'>('emcall.education-media.net')
  const [showSipPasswordModal, setShowSipPasswordModal] = useState(false)
  const [sipPassword, setSipPassword] = useState('')
  const [showTailscaleModal, setShowTailscaleModal] = useState(false)
  const [tailscaleAuthkey, setTailscaleAuthkey] = useState('')
  const [tailscaleHostname, setTailscaleHostname] = useState('')
  const [showActivityWatchModal, setShowActivityWatchModal] = useState(false)
  // Mirrors AW_VERSION_DEFAULT in agent/commands/command_handler.py — bump together
  const [awVersion, setAwVersion] = useState('v0.13.2')
  const [awSha256, setAwSha256] = useState('')
  const [awWithExtension, setAwWithExtension] = useState(true)
  const [showActivityWatchUninstallModal, setShowActivityWatchUninstallModal] = useState(false)
  const [awPurgeData, setAwPurgeData] = useState(false)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [operatorMessage, setOperatorMessage] = useState('')
  const [messageSeverity, setMessageSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium')
  const [wifiReport, setWifiReport] = useState<Record<string, unknown> | null>(null)

  // Get current machine data
  const { data: machines = [] } = useQuery({
    queryKey: ['machines-with-metrics'],
    queryFn: getMachinesWithMetrics,
    refetchInterval: 10000,
  })

  const machine = machines.find((m: MachineWithMetrics) => m.machine_id === machineId)

  // Get VoIP state from WebSocket context
  const { voipStates } = useWebSocket()
  const extension = machineId?.replace('operator-', '') || ''
  const voipState = voipStates[extension]

  // Get operator data for audio config
  const { data: operator } = useQuery({
    queryKey: ['operator-by-machine', machineId],
    queryFn: () => getOperatorByMachineId(machineId!),
    enabled: !!machineId,
  })

  // Get metrics history
  const { data: metricsHistory = [], isLoading: isLoadingHistory } = useQuery({
    queryKey: ['metrics-history', machineId, period],
    queryFn: () => getMachineMetricsHistory(machineId!, period),
    enabled: !!machineId,
  })

  // Get network history
  const { data: networkHistory = [], isLoading: isLoadingNetwork } = useQuery({
    queryKey: ['network-history', machineId],
    queryFn: () => getNetworkHistory(machineId!),
    enabled: !!machineId,
  })

  // Get audio history
  const { data: audioHistory = [], isLoading: isLoadingAudio } = useQuery({
    queryKey: ['audio-history', machineId],
    queryFn: () => getAudioHistory(machineId!),
    enabled: !!machineId,
  })

  // Get charger (battery plugged) history
  const { data: batteryHistory = [] } = useQuery({
    queryKey: ['battery-history', machineId],
    queryFn: () => getBatteryHistory(machineId!),
    enabled: !!machineId,
  })

  // Get audio metrics history (for volume graphs)
  const { data: audioMetricsHistory = [], isLoading: isLoadingAudioMetrics } = useQuery({
    queryKey: ['audio-metrics-history', machineId, period],
    queryFn: () => getAudioMetricsHistory(machineId!, period),
    enabled: !!machineId,
  })

  // Get audio device changes history
  const { data: audioDevicesHistory = [], isLoading: isLoadingAudioDevices } = useQuery({
    queryKey: ['audio-devices-history', machineId],
    queryFn: () => getAudioDevicesHistory(machineId!),
    enabled: !!machineId,
  })

  // Activity (ActivityWatch) — admin/manager only, mirrors server require_role
  // Own period state: the Activité tab has its own selector and must not
  // silently retune the Vue d'ensemble charts (and vice-versa).
  const [activityPeriod, setActivityPeriod] = useState<ActivityPeriod>('24h')
  // Bucketed to 5 minutes: an un-memoized `new Date()` here produces a fresh
  // queryKey on every render, and this page re-renders every 5 s — React Query
  // would then refetch the timeline in a loop.
  const activityRangeBucket = Math.floor(Date.now() / 300_000)
  const activityTimelineRange = useMemo(() => {
    const hours = ACTIVITY_PERIOD_HOURS[activityPeriod]
    const end = new Date(activityRangeBucket * 300_000)
    const start = new Date(end.getTime() - hours * 3600 * 1000)
    return { start: start.toISOString(), end: end.toISOString() }
  }, [activityPeriod, activityRangeBucket])

  const { data: activitySummary, isLoading: isLoadingActivitySummary } = useQuery({
    queryKey: ['activity-summary', machineId, activityPeriod],
    queryFn: () => getActivitySummary(machineId!, activityPeriod),
    enabled: !!machineId && canViewActivity,
  })

  const { data: activityTimeline, isLoading: isLoadingActivityTimeline, error: activityTimelineError } = useQuery({
    queryKey: ['activity-timeline', machineId, activityTimelineRange.start, activityTimelineRange.end],
    queryFn: () => getActivityTimeline(machineId!, activityTimelineRange.start, activityTimelineRange.end, 'hour'),
    enabled: !!machineId && canViewActivity,
  })

  // Derived from the data, not from the label map: the agent can emit
  // categories the map does not know about, and a hard-coded list drops them
  // from the stacked chart entirely.
  const activityTimelineCategories = useMemo(() => {
    const seen = new Set<string>()
    for (const point of activityTimeline?.points ?? []) {
      for (const category of Object.keys(point.durations)) seen.add(activityCategoryLabel(category))
    }
    return [...seen]
  }, [activityTimeline])

  const { data: focusCorrelation, isLoading: isLoadingFocusCorrelation } = useQuery({
    queryKey: ['focus-correlation', machineId],
    queryFn: () => getFocusCorrelation(machineId!, 7),
    enabled: !!machineId && canViewActivity,
  })

  // Get command history
  const { data: commandsData } = useQuery({
    queryKey: ['command-history', machineId],
    queryFn: () => getCommandHistory(machineId!, 20),
    enabled: !!machineId,
    refetchInterval: 5000,
  })

  // Get deep audio diagnostics list
  const { data: deepAudioDiagnostics } = useQuery<DiagnosticSummary[]>({
    queryKey: ['deep-audio-diagnostics', machineId],
    queryFn: () => listDiagnostics({ machine_id: machineId!, trigger: 'deep_audio_manual', days: 30, limit: 20 }),
    enabled: !!machineId,
    refetchInterval: 10000,
  })

  // Extract major events
  const majorEvents = extractMajorEvents(
    networkHistory,
    audioHistory,
    batteryHistory,
    commandsData?.commands || []
  )

  // Command mutations
  const commandMutation = useMutation({
    mutationFn: ({ type, payload, queueIfOffline }: { type: CommandType; payload?: Record<string, unknown>; queueIfOffline?: boolean }) =>
      executeCommand(machineId!, type, payload, queueIfOffline),
    onSuccess: (data, variables) => {
      if (variables.type === 'install_linphone') {
        setShowLinphoneModal(false)
        setLinphonePassword('')
      }
      if (variables.type === 'notify_operator') {
        setShowMessageModal(false)
        setOperatorMessage('')
      }
      if (variables.type === 'install_activitywatch') {
        setShowActivityWatchModal(false)
        setAwSha256('')
      }
      if (variables.type === 'uninstall_activitywatch') {
        setShowActivityWatchUninstallModal(false)
      }
      if (data.status === 'queued') {
        setCommandMessage({ type: 'success', text: '⏳ Agent hors ligne — commande en file d\'attente, elle partira à sa prochaine connexion (validité 24h)' })
        queryClient.invalidateQueries({ queryKey: ['command-history', machineId] })
        setTimeout(() => setCommandMessage(null), 8000)
        return
      }
      const messages: Record<string, string> = {
        speedtest: 'Test de vitesse lancé',
        restart_network: 'Redémarrage réseau lancé',
        get_logs: 'Demande de logs envoyée',
        update_agent: 'Mise à jour de l\'agent lancée',
        clean_linphone: 'Nettoyage Linphone lancé',
        install_linphone: 'Installation Linphone lancée',
        fix_audio_config: 'Reconfiguration audio lancée',
        setup_echo_cancel: 'Configuration anti-écho lancée (peut prendre ~10s)',
        deep_audio_diagnostic: 'Diagnostic audio profond lancé (30-60s)',
        install_wifi_indicator: 'Installation indicateur WiFi lancée',
        uninstall_wifi_indicator: 'Désinstallation indicateur WiFi lancée',
        disable_wifi_powersave: 'Désactivation WiFi powersave (brève coupure WiFi)',
        set_audio_codec: 'Changement de codec audio lancé (redémarre Linphone)',
        notify_operator: "Message envoyé à l'opératrice",
        force_emcall: 'Forçage EMCALL lancé (redémarre Linphone)',
        wifi_report: 'Rapport WiFi demandé (résultat dans ~15s)',
        install_activitywatch: 'Installation ActivityWatch lancée (peut prendre quelques minutes)',
        uninstall_activitywatch: 'Désinstallation ActivityWatch lancée',
      }
      setCommandMessage({ type: 'success', text: messages[variables.type] || 'Commande envoyée' })
      queryClient.invalidateQueries({ queryKey: ['command-history', machineId] })
      setTimeout(() => setCommandMessage(null), 5000)
    },
    onError: (error: any) => {
      const detail = formatErrorDetail(error.response?.data?.detail, error.message || 'Erreur lors de l\'envoi de la commande')
      setCommandMessage({ type: 'error', text: detail })
      setTimeout(() => setCommandMessage(null), 5000)
    },
  })

  // Save SIP password mutation
  const sipPasswordMutation = useMutation({
    mutationFn: (password: string) => updateOperator(operator!.id, { sip_password: password }),
    onSuccess: () => {
      setCommandMessage({ type: 'success', text: 'Mot de passe SIP enregistré' })
      setShowSipPasswordModal(false)
      setSipPassword('')
      setTimeout(() => setCommandMessage(null), 5000)
    },
    onError: (error: any) => {
      const detail = formatErrorDetail(error.response?.data?.detail, error.message || 'Erreur lors de la sauvegarde du mot de passe SIP')
      setCommandMessage({ type: 'error', text: detail })
      setTimeout(() => setCommandMessage(null), 5000)
    },
  })

  // Install Tailscale mutation
  const installTailscaleMutation = useMutation({
    mutationFn: ({ authkey, hostname }: { authkey: string; hostname: string }) =>
      installTailscale(machineId!, authkey, hostname),
    onSuccess: () => {
      setCommandMessage({
        type: 'success',
        text: 'Installation Tailscale lancée. Peut prendre jusqu\'à 5 minutes.',
      })
      setShowTailscaleModal(false)
      setTailscaleAuthkey('')
      setTailscaleHostname('')
      queryClient.invalidateQueries({ queryKey: ['command-history', machineId] })
      setTimeout(() => setCommandMessage(null), 8000)
    },
    onError: (error: any) => {
      const detail = formatErrorDetail(error.response?.data?.detail, error.message || 'Erreur lors de l\'installation Tailscale')
      setCommandMessage({ type: 'error', text: detail })
      setTimeout(() => setCommandMessage(null), 5000)
    },
  })

  // Uninstall Tailscale mutation
  const uninstallTailscaleMutation = useMutation({
    mutationFn: () => uninstallTailscale(machineId!, false),
    onSuccess: () => {
      setCommandMessage({ type: 'success', text: 'Déconnexion Tailscale lancée' })
      queryClient.invalidateQueries({ queryKey: ['command-history', machineId] })
      queryClient.invalidateQueries({ queryKey: ['operator-by-machine'] })
      setTimeout(() => setCommandMessage(null), 5000)
    },
    onError: (error: any) => {
      const detail = formatErrorDetail(error.response?.data?.detail, error.message || 'Erreur lors de la déconnexion Tailscale')
      setCommandMessage({ type: 'error', text: detail })
      setTimeout(() => setCommandMessage(null), 5000)
    },
  })

  // Transform data for charts - keep null values to show gaps in data
  const chartData = metricsHistory.map((m: MetricsHistory) => ({
    time: formatTimeForPeriod(m.time, period),
    CPU: m.cpu_avg,
    RAM: m.ram_avg,
    'Signal WiFi': m.wifi_signal_avg,
    Ping: m.ping_avg,
    Jitter: m.jitter_avg,
    Batterie: m.battery_avg,
  }))

  // Transform audio metrics for charts
  const audioChartData = audioMetricsHistory.map((m: AudioMetricsHistory) => ({
    time: formatTimeForPeriod(m.time, period),
    'Vol. Sortie': m.output_volume_avg,
    'Vol. Micro': m.input_volume_avg,
    'Sortie Muet %': m.output_muted_pct,
    'Micro Muet %': m.input_muted_pct,
  }))

  if (!machine) {
    return (
      <div className="p-6">
        <Button
          icon={ArrowLeftIcon}
          variant="light"
          onClick={() => navigate('/realtime')}
          className="mb-4"
        >
          Retour
        </Button>
        <Card>
          <Text className="text-gray-500">Opérateur non trouvé</Text>
        </Card>
      </div>
    )
  }

  const metrics = machine.metrics
  const isOnline = machine.status === 'online'
  const emcall = getEmcallStatus(metrics)
  const emcallDrifted = emcall.known && !emcall.ok

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button
          icon={ArrowLeftIcon}
          variant="light"
          onClick={() => navigate('/realtime')}
          className="!min-h-[40px] flex-shrink-0"
        >
          <span className="hidden sm:inline">Retour</span>
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <Title className="!text-lg sm:!text-2xl truncate">{machine.operator_name || machine.name}</Title>
            <Badge color={getMachineStatusColor(machine.status)} size="lg">
              {getMachineStatusLabel(machine.status)}
            </Badge>
            {voipState && (
              <PhoneIndicator
                status={voipState.status}
                isInCall={voipState.is_in_call}
              />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
            <Flex justifyContent="start" className="gap-1">
              <MapPinIcon className="w-4 h-4 text-gray-400" />
              <Text className="text-gray-500 text-sm">{machine.location}</Text>
            </Flex>
            <Flex justifyContent="start" className="gap-1">
              <ComputerDesktopIcon className="w-4 h-4 text-gray-400" />
              <Text className="text-gray-500 text-sm truncate">{machine.machine_id}</Text>
            </Flex>
            <Flex justifyContent="start" className="gap-1">
              <ClockIcon className="w-4 h-4 text-gray-400" />
              <Text className="text-gray-500 text-sm">
                Dernière activité: {formatDate(machine.last_seen)}
              </Text>
            </Flex>
            {operator?.agent_version && (
              <Badge color="gray" size="xs">
                Agent v{operator.agent_version}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {emcallDrifted && (
        <Callout
          title={`Périphérique audio dérivé — ${emcallDriftLabel(emcall)}`}
          color="red"
          icon={ExclamationTriangleIcon}
          className="mb-4"
        >
          Le périphérique par défaut a basculé hors EMCALL (anti-écho). L'opératrice utilise
          {!emcall.micOk && ` le micro « ${metrics?.input_device} »`}
          {!emcall.micOk && !emcall.outOk && ' et'}
          {!emcall.outOk && ` la sortie « ${metrics?.output_device} »`}
          {' '}au lieu des périphériques EMCALL.
          <div className="mt-3">
            <Button
              size="xs"
              color="red"
              icon={SpeakerWaveIcon}
              disabled={commandMutation.isPending}
              onClick={() => {
                if (!isOnline && !confirm("Agent hors ligne — mettre la commande en file d'attente ? Elle partira automatiquement à sa prochaine connexion (validité 24h).")) return
                commandMutation.mutate({ type: 'force_emcall', queueIfOffline: true })
              }}
            >
              {isOnline ? 'Forcer EMCALL maintenant' : 'Forcer EMCALL (différé)'}
            </Button>
          </div>
        </Callout>
      )}

      {/* Current Metrics Cards - Priority: Battery, Audio, Network */}
      <Grid numItemsSm={2} numItemsMd={3} className="gap-4">
        {/* Battery / Power - Most important */}
        <Card
          decoration="top"
          decorationColor={
            metrics?.has_battery === false
              ? 'blue'
              : metrics?.battery_plugged
                ? 'green'
                : (metrics?.battery_percent ?? 100) < 20
                  ? 'red'
                  : 'yellow'
          }
        >
          <Flex justifyContent="start" className="gap-2">
            {metrics?.has_battery === false ? (
              <ComputerDesktopIcon className="w-5 h-5 text-blue-500" />
            ) : metrics?.battery_plugged ? (
              <BoltIcon className="w-5 h-5 text-green-500" />
            ) : (
              <Battery100Icon className="w-5 h-5 text-yellow-500" />
            )}
            <Text>Alimentation</Text>
          </Flex>
          {metrics?.has_battery === false ? (
            <>
              <Metric className="text-blue-600">Secteur</Metric>
              <Text className="text-sm text-gray-500 mt-1">PC fixe - Pas de batterie</Text>
            </>
          ) : (
            <>
              <Metric className={
                metrics?.battery_plugged
                  ? 'text-green-600'
                  : (metrics?.battery_percent ?? 100) < 20
                    ? 'text-red-600'
                    : 'text-yellow-600'
              }>
                {metrics?.battery_percent ?? '-'}%
              </Metric>
              {metrics && (
                <>
                  <ProgressBar
                    value={metrics.battery_percent ?? 0}
                    color={
                      metrics.battery_plugged
                        ? 'green'
                        : metrics.battery_percent !== null && metrics.battery_percent < 20
                          ? 'red'
                          : 'yellow'
                    }
                    className="mt-2"
                  />
                  <Text className="text-sm mt-2">
                    {metrics.battery_plugged ? (
                      <span className="text-green-600">⚡ Branché sur secteur</span>
                    ) : (
                      <span className="text-yellow-600">🔋 Sur batterie</span>
                    )}
                  </Text>
                </>
              )}
            </>
          )}
        </Card>

        {/* Audio */}
        <Card
          decoration="top"
          decorationColor={
            emcallDrifted
              ? 'red'
              : metrics?.input_device_match === false || metrics?.output_device_match === false
                ? 'orange'
                : metrics?.mic_muted ? 'red' : 'green'
          }
        >
          <Flex justifyContent="between">
            <Flex justifyContent="start" className="gap-2">
              <MicrophoneIcon className="w-5 h-5 text-gray-500" />
              <Text>Audio</Text>
              {emcallDrifted && (
                <Badge color="red" size="xs">{emcallDriftLabel(emcall)}</Badge>
              )}
              {!emcallDrifted && (metrics?.input_device_match === false || metrics?.output_device_match === false) && (
                <Badge color="orange" size="xs">Mauvais device</Badge>
              )}
            </Flex>
            <Button
              size="xs"
              variant="light"
              icon={Cog6ToothIcon}
              onClick={(e) => { e.stopPropagation(); setShowAudioConfig(true); }}
              title="Configurer les périphériques audio attendus"
            />
          </Flex>
          <Flex className="mt-2 gap-4">
            <div>
              <Text className="text-xs text-gray-500">Micro</Text>
              <Text className="font-medium">
                {metrics?.mic_muted ? '🔇 Muet' : metrics?.mic_volume != null ? `🎤 ${metrics.mic_volume}%` : '🎤 --'}
              </Text>
              {metrics?.input_device && (
                <Flex justifyContent="start" className="gap-1">
                  {!emcall.micOk ? (
                    <ExclamationTriangleIcon className="w-3 h-3 text-red-500" />
                  ) : metrics.input_device_match === false ? (
                    <ExclamationTriangleIcon className="w-3 h-3 text-orange-500" />
                  ) : (
                    <CheckCircleIcon className="w-3 h-3 text-green-500" />
                  )}
                  <Text
                    className={`text-xs break-all ${
                      !emcall.micOk ? 'text-red-500' :
                      metrics.input_device_match === false ? 'text-orange-500' : 'text-gray-400'
                    }`}
                  >
                    {metrics.input_device}
                  </Text>
                </Flex>
              )}
            </div>
            <div>
              <Text className="text-xs text-gray-500">Sortie</Text>
              <Text className="font-medium">
                {metrics?.output_muted ? '🔇 Muet' : metrics?.output_volume != null ? `🔊 ${metrics.output_volume}%` : '🔊 --'}
              </Text>
              {metrics?.output_device && (
                <Flex justifyContent="start" className="gap-1">
                  {!emcall.outOk ? (
                    <ExclamationTriangleIcon className="w-3 h-3 text-red-500" />
                  ) : metrics.output_device_match === false ? (
                    <ExclamationTriangleIcon className="w-3 h-3 text-orange-500" />
                  ) : (
                    <CheckCircleIcon className="w-3 h-3 text-green-500" />
                  )}
                  <Text
                    className={`text-xs break-all ${
                      !emcall.outOk ? 'text-red-500' :
                      metrics.output_device_match === false ? 'text-orange-500' : 'text-gray-400'
                    }`}
                  >
                    {metrics.output_device}
                  </Text>
                </Flex>
              )}
            </div>
          </Flex>
          {/* Linphone stream info (during active call) */}
          {metrics?.linphone_stream_active && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <Flex justifyContent="start" className="gap-2 mb-1">
                <PhoneIcon className="w-3 h-3 text-green-400" />
                <Text className="text-xs text-green-400 font-medium">Linphone (en appel)</Text>
              </Flex>
              <Flex className="gap-4">
                <Text className="text-xs">
                  Micro: <span className="font-medium">{metrics?.linphone_stream_capture_vol ?? '--'}%</span>
                </Text>
                <Text className="text-xs">
                  Sortie: <span className="font-medium">{metrics?.linphone_stream_playback_vol ?? '--'}%</span>
                </Text>
              </Flex>
            </div>
          )}
          {metrics?.linphone_mic_gain_db != null && !metrics?.linphone_stream_active && (
            <div className="mt-3 pt-3 border-t border-gray-700">
              <Text className="text-xs text-gray-500">
                Linphone config: micro {metrics?.linphone_mic_gain_db} dB | sortie {metrics?.linphone_playback_gain_db} dB
              </Text>
            </div>
          )}
        </Card>

        {/* Network */}
        {(() => {
          const nq = metrics?.network_quality ?? null
          const qVisual = getQualityVisual(nq?.level)
          const isEthernet = metrics?.network_type === 'ethernet'
          // Prefer the composite quality level for the decoration colour
          // (matches the operator-side indicator); fall back to the legacy
          // wifi-signal mapping when the agent has not shipped a quality
          // block yet.
          const decorationColor = nq?.level
            ? qVisual.tremor
            : metrics?.wifi_signal
              ? metrics.wifi_signal >= 70
                ? 'green'
                : metrics.wifi_signal >= 50
                  ? 'yellow'
                  : 'red'
              : 'gray'
          return (
            <Card decoration="top" decorationColor={decorationColor}>
              <Flex justifyContent="between" alignItems="start">
                <Flex justifyContent="start" className="gap-2">
                  {isEthernet ? (
                    <LinkIcon className="w-5 h-5 text-gray-500" />
                  ) : (
                    <WifiIcon className="w-5 h-5 text-gray-500" />
                  )}
                  <Text>Réseau</Text>
                </Flex>
                <Flex justifyContent="end" className="gap-1 flex-wrap">
                  {nq?.connection_type === 'mobile_hotspot' && (
                    <Badge color="orange" title="Partage 4G téléphone — qualité instable">
                      📱 Hotspot
                    </Badge>
                  )}
                  {nq?.level && (
                    <Badge color={qVisual.tremor} title={nq.verbal_reason || ''}>
                      {qVisual.dot} {nq.medium_label ? `${nq.medium_label} ` : ''}{nq.level_label}
                    </Badge>
                  )}
                </Flex>
              </Flex>
              {isEthernet ? (
                <>
                  <Metric>🔌 Câble</Metric>
                  <Text className="text-xs text-gray-500 mt-1">Connexion filaire Ethernet</Text>
                </>
              ) : (
                <>
                  <Metric>{metrics?.wifi_signal ?? '-'}%</Metric>
                  {metrics?.wifi_ssid && (
                    <Text className="text-xs text-gray-500 mt-1">{metrics.wifi_ssid}</Text>
                  )}
                </>
              )}
              <Flex className="mt-2 gap-4">
                <Text className="text-xs">
                  Ping: <span className="font-medium">{metrics?.ping_ms ? `${Math.round(metrics.ping_ms)}ms` : '-'}</span>
                </Text>
                <Text className="text-xs">
                  Jitter: <span className="font-medium">{metrics?.jitter_ms ? `${Math.round(metrics.jitter_ms)}ms` : '-'}</span>
                </Text>
              </Flex>
              {metrics?.network_type === 'wifi' && metrics?.wifi_powersave != null && (
                <Text className="text-xs mt-1">
                  Économie d'énergie WiFi :{' '}
                  {metrics.wifi_powersave ? (
                    <span
                      className="font-medium text-amber-600 dark:text-amber-400"
                      title="Le power save WiFi cause des pics de latence en VoIP — utilisez « Disable WiFi Powersave »"
                    >
                      ⚠ Activée
                    </span>
                  ) : (
                    <span className="font-medium text-green-600 dark:text-green-400">Désactivée</span>
                  )}
                </Text>
              )}
              {nq?.level && (nq.verbal_reason || nq.verbal_action) && (
                <div
                  className="mt-3 rounded-md p-2 text-xs"
                  style={{ backgroundColor: qVisual.cssBg, color: qVisual.cssText }}
                >
                  {nq.verbal_reason && <div className="font-medium">{nq.verbal_reason}</div>}
                  {nq.verbal_action && <div className="italic mt-1">{nq.verbal_action}</div>}
                  {nq.alternatives && nq.alternatives.length > 0 && (
                    <div className="mt-1">
                      Alternative : <span className="font-medium">{nq.alternatives[0].ssid}</span>
                      {' '}(+{nq.alternatives[0].delta_db} dBm)
                    </div>
                  )}
                </div>
              )}
            </Card>
          )
        })()}
      </Grid>

      {/* System metrics - Secondary importance */}
      <Card>
        <Flex justifyContent="start" className="gap-2 mb-4">
          <CpuChipIcon className="w-5 h-5 text-gray-500" />
          <Text className="font-medium">Système</Text>
        </Flex>
        <Grid numItemsSm={2} className="gap-6">
          <div>
            <Flex justifyContent="between" className="mb-1">
              <Text className="text-sm">CPU</Text>
              <Text className="text-sm font-medium">{metrics?.cpu_percent ?? '-'}%</Text>
            </Flex>
            <ProgressBar
              value={metrics?.cpu_percent ?? 0}
              color={metrics ? getProgressColor(metrics.cpu_percent, { warning: 70, danger: 90 }) : 'gray'}
            />
          </div>
          <div>
            <Flex justifyContent="between" className="mb-1">
              <Text className="text-sm">RAM</Text>
              <Text className="text-sm font-medium">{metrics?.ram_percent ?? '-'}%</Text>
            </Flex>
            <ProgressBar
              value={metrics?.ram_percent ?? 0}
              color={metrics ? getProgressColor(metrics.ram_percent, { warning: 80, danger: 95 }) : 'gray'}
            />
          </div>
        </Grid>
      </Card>

      {/* Alerts */}
      {metrics && (
        <div className="space-y-2">
          {metrics.ping_ms && metrics.ping_ms > 150 && (
            <Callout title="Latence élevée" color="yellow" icon={ExclamationTriangleIcon}>
              La latence est de {Math.round(metrics.ping_ms)} ms (seuil: 150 ms)
            </Callout>
          )}
          {metrics.jitter_ms && metrics.jitter_ms > 30 && (
            <Callout title="Jitter élevé" color="yellow" icon={ExclamationTriangleIcon}>
              Le jitter est de {Math.round(metrics.jitter_ms)} ms (seuil: 30 ms)
            </Callout>
          )}
          {metrics.mic_muted && (
            <Callout title="Microphone muet" color="red" icon={ExclamationTriangleIcon}>
              Le microphone est en sourdine
            </Callout>
          )}
          {(metrics.input_device_match === false || metrics.output_device_match === false) && (
            <Callout title="Mauvais périphérique audio" color="orange" icon={ExclamationTriangleIcon}>
              L'opérateur n'utilise pas le casque attendu
              {metrics.input_device_match === false && metrics.input_device && (
                <span className="block text-sm mt-1">Micro: {metrics.input_device}</span>
              )}
              {metrics.output_device_match === false && metrics.output_device && (
                <span className="block text-sm mt-1">Sortie: {metrics.output_device}</span>
              )}
            </Callout>
          )}
        </div>
      )}

      {/* Main Tabs */}
      <TabGroup index={mainTab} onIndexChange={setMainTab}>
        <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
          <TabList className="!flex-nowrap min-w-max">
            {[
              <Tab key="overview">Vue d'ensemble</Tab>,
              <Tab key="events">Événements majeurs</Tab>,
              <Tab key="history">Historique détaillé</Tab>,
              ...(canWrite ? [<Tab key="actions">Actions & Commandes</Tab>] : []),
              ...(canViewActivity ? [<Tab key="activity">Activité</Tab>] : []),
            ]}
          </TabList>
        </div>

        <TabPanels>
          {/* Tab 1: Overview */}
          <TabPanel>
            <div className="space-y-6 mt-6">
              {/* VoIP Call Timeline */}
              {extension && extension.length === 6 && (
                <CallTimeline extension={extension} hours={8} />
              )}

              {/* Historical Charts */}
              <Card>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                  <Title>Historique des métriques</Title>
                  <Select value={period} onValueChange={(v) => setPeriod(v as Period)} className="w-full sm:w-32">
                    <SelectItem value="4h">4 heures</SelectItem>
                    <SelectItem value="24h">24 heures</SelectItem>
                    <SelectItem value="7d">7 jours</SelectItem>
                    <SelectItem value="30d">30 jours</SelectItem>
                  </Select>
                </div>

                {isLoadingHistory ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                  </div>
                ) : chartData.length === 0 ? (
                  <Text className="text-gray-500 text-center py-8">Aucune donnée disponible</Text>
                ) : (
                  <TabGroup>
                    <div className="overflow-x-auto mb-4">
                      <TabList className="!flex-nowrap min-w-max">
                        <Tab>Audio</Tab>
                        <Tab>Batterie</Tab>
                        <Tab>Réseau</Tab>
                        <Tab>Système</Tab>
                      </TabList>
                    </div>
                    <TabPanels>
                      <TabPanel>
                        {isLoadingAudioMetrics ? (
                          <div className="flex items-center justify-center h-72">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
                          </div>
                        ) : audioChartData.length === 0 ? (
                          <Text className="text-gray-500 text-center py-8">Aucune donnée audio disponible</Text>
                        ) : (
                          <div className="space-y-6">
                            {/* Volume Graph */}
                            <div>
                              <Text className="text-sm font-medium text-gray-600 mb-2">Volume (%)</Text>
                              <AreaChart
                                className="h-72"
                                data={audioChartData}
                                index="time"
                                categories={['Vol. Sortie', 'Vol. Micro']}
                                colors={['violet', 'fuchsia']}
                                valueFormatter={(v: number) => `${v}%`}
                                showLegend
                                showGridLines
                                curveType="monotone"
                                connectNulls={false}
                              />
                            </div>
                            {/* Mute percentage graph */}
                            <div>
                              <Text className="text-sm font-medium text-gray-600 mb-2">Temps en sourdine (% du bucket)</Text>
                              <AreaChart
                                className="h-48"
                                data={audioChartData}
                                index="time"
                                categories={['Sortie Muet %', 'Micro Muet %']}
                                colors={['rose', 'red']}
                                valueFormatter={(v: number) => `${Math.round(v)}%`}
                                showLegend
                                showGridLines
                                curveType="monotone"
                                connectNulls={false}
                              />
                            </div>
                          </div>
                        )}
                      </TabPanel>
                      <TabPanel>
                        <AreaChart
                          className="h-72"
                          data={chartData}
                          index="time"
                          categories={['Batterie']}
                          colors={['emerald']}
                          valueFormatter={(v: number) => `${v}%`}
                          showLegend
                          showGridLines
                          curveType="monotone"
                          connectNulls={false}
                        />
                      </TabPanel>
                      <TabPanel>
                        <AreaChart
                          className="h-72"
                          data={chartData}
                          index="time"
                          categories={['Signal WiFi', 'Ping', 'Jitter']}
                          colors={['green', 'orange', 'red']}
                          valueFormatter={(v: number) => `${v}`}
                          showLegend
                          showGridLines
                          curveType="monotone"
                          connectNulls={false}
                        />
                      </TabPanel>
                      <TabPanel>
                        <AreaChart
                          className="h-72"
                          data={chartData}
                          index="time"
                          categories={['CPU', 'RAM']}
                          colors={['blue', 'cyan']}
                          valueFormatter={(v: number) => `${v}%`}
                          showLegend
                          showGridLines
                          curveType="monotone"
                          connectNulls={false}
                        />
                      </TabPanel>
                    </TabPanels>
                  </TabGroup>
                )}
              </Card>

              {/* Recent Events Summary */}
              <Card>
                <Title className="mb-4">Événements récents</Title>
                {majorEvents.length === 0 ? (
                  <Text className="text-gray-500 text-center py-4">Aucun événement majeur</Text>
                ) : (
                  <div className="space-y-2">
                    {majorEvents.slice(0, 5).map((event, idx) => {
                      const Icon = getEventIcon(event.type)
                      return (
                        <Flex key={idx} justifyContent="start" className="gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-800">
                          <Badge color={getEventColor(event.severity)} icon={Icon} />
                          <div className="flex-1">
                            <Text className="font-medium">{event.title}</Text>
                            <Text className="text-xs text-gray-500">{event.description}</Text>
                          </div>
                          <Text className="text-xs text-gray-400">{formatDateTime(event.time)}</Text>
                        </Flex>
                      )
                    })}
                    {majorEvents.length > 5 && (
                      <Button variant="light" onClick={() => setMainTab(1)} className="w-full mt-2">
                        Voir tous les événements ({majorEvents.length})
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            </div>
          </TabPanel>

          {/* Tab 2: Major Events */}
          <TabPanel>
            <Card className="mt-6">
              <Flex justifyContent="between" className="mb-4">
                <Title>Événements majeurs</Title>
                <Badge color="blue">{majorEvents.length} événements</Badge>
              </Flex>

              {majorEvents.length === 0 ? (
                <Text className="text-gray-500 text-center py-8">
                  Aucun événement majeur détecté dans les dernières 24h
                </Text>
              ) : (
                <div className="space-y-3">
                  {majorEvents.map((event, idx) => {
                    const Icon = getEventIcon(event.type)
                    const correlationWindow = canViewActivity && event.type === 'quality_degradation'
                      ? findCorrelationWindow(focusCorrelation?.windows, event.time)
                      : null
                    return (
                      <div
                        key={idx}
                        className={`p-4 rounded-lg border-l-4 ${
                          event.severity === 'error' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' :
                          event.severity === 'warning' ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' :
                          event.severity === 'success' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' :
                          'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        }`}
                      >
                        <Flex justifyContent="between" alignItems="start">
                          <Flex justifyContent="start" className="gap-3">
                            <Icon className={`w-5 h-5 ${
                              event.severity === 'error' ? 'text-red-500' :
                              event.severity === 'warning' ? 'text-yellow-500' :
                              event.severity === 'success' ? 'text-green-500' :
                              'text-blue-500'
                            }`} />
                            <div>
                              <Text className="font-semibold">{event.title}</Text>
                              <Text className="text-sm text-gray-600 dark:text-gray-400">{event.description}</Text>
                              {correlationWindow && (
                                <Text className="text-xs text-gray-500 mt-1">
                                  Contexte : {correlationWindow.verdict}
                                </Text>
                              )}
                            </div>
                          </Flex>
                          <Text className="text-sm text-gray-500 whitespace-nowrap ml-4">
                            {formatDateTime(event.time)}
                          </Text>
                        </Flex>
                      </div>
                    )
                  })}
                </div>
              )}
            </Card>
          </TabPanel>

          {/* Tab 3: Detailed History */}
          <TabPanel>
            <Card className="mt-6">
              <TabGroup>
                <div className="overflow-x-auto">
                  <TabList className="!flex-nowrap min-w-max">
                    <Tab icon={GlobeAltIcon}>Réseau</Tab>
                    <Tab icon={SpeakerWaveIcon}>Audio</Tab>
                    <Tab icon={MicrophoneIcon}>Périphériques audio (7j)</Tab>
                  </TabList>
                </div>
                <TabPanels>
                  {/* Sub-tab: Network */}
                  <TabPanel>
                    {isLoadingNetwork ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                      </div>
                    ) : networkHistory.length === 0 ? (
                      <Text className="text-gray-500 text-center py-8">Aucun historique réseau disponible</Text>
                    ) : (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>Début</TableHeaderCell>
                            <TableHeaderCell>Fin</TableHeaderCell>
                            <TableHeaderCell>Type</TableHeaderCell>
                            <TableHeaderCell>SSID</TableHeaderCell>
                            <TableHeaderCell>Signal</TableHeaderCell>
                            <TableHeaderCell>Ping</TableHeaderCell>
                            <TableHeaderCell>Jitter</TableHeaderCell>
                            <TableHeaderCell>Débit</TableHeaderCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {networkHistory.map((entry: NetworkHistoryEntry, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{formatDateTime(entry.start)}</TableCell>
                              <TableCell>{entry.end ? formatDateTime(entry.end) : <Badge color="green" size="xs">En cours</Badge>}</TableCell>
                              <TableCell>
                                <Flex justifyContent="start" className="gap-1">
                                  {entry.network_type === 'wifi' ? (
                                    <WifiIcon className="w-4 h-4 text-blue-500" />
                                  ) : (
                                    <GlobeAltIcon className="w-4 h-4 text-green-500" />
                                  )}
                                  <Text>{entry.network_type === 'wifi' ? 'WiFi' : 'Ethernet'}</Text>
                                </Flex>
                              </TableCell>
                              <TableCell>{entry.wifi_ssid || '—'}</TableCell>
                              <TableCell>
                                {entry.wifi_signal !== null ? (
                                  <Badge color={entry.wifi_signal >= 70 ? 'green' : entry.wifi_signal >= 50 ? 'yellow' : 'red'}>
                                    {entry.wifi_signal}%
                                  </Badge>
                                ) : '—'}
                              </TableCell>
                              <TableCell>
                                {entry.ping_ms !== null ? (
                                  <Badge color={entry.ping_ms < 50 ? 'green' : entry.ping_ms < 100 ? 'yellow' : 'red'}>
                                    {Math.round(entry.ping_ms)} ms
                                  </Badge>
                                ) : '—'}
                              </TableCell>
                              <TableCell>
                                {entry.jitter_ms !== null ? (
                                  <Badge color={entry.jitter_ms < 20 ? 'green' : entry.jitter_ms < 50 ? 'yellow' : 'red'}>
                                    {Math.round(entry.jitter_ms)} ms
                                  </Badge>
                                ) : '—'}
                              </TableCell>
                              <TableCell>
                                {entry.download_mbps !== null && entry.upload_mbps !== null ? (
                                  <Text className="text-xs">
                                    ↓{Math.round(entry.download_mbps)} ↑{Math.round(entry.upload_mbps)}
                                  </Text>
                                ) : '—'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabPanel>

                  {/* Sub-tab: Audio */}
                  <TabPanel>
                    {isLoadingAudio ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500" />
                      </div>
                    ) : audioHistory.length === 0 ? (
                      <Text className="text-gray-500 text-center py-8">Aucun historique audio disponible</Text>
                    ) : (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableHeaderCell>Début</TableHeaderCell>
                            <TableHeaderCell>Fin</TableHeaderCell>
                            <TableHeaderCell>Sortie</TableHeaderCell>
                            <TableHeaderCell>Périph. sortie</TableHeaderCell>
                            <TableHeaderCell>Micro</TableHeaderCell>
                            <TableHeaderCell>Périph. entrée</TableHeaderCell>
                            <TableHeaderCell>USB</TableHeaderCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {audioHistory.map((entry: AudioHistoryEntry, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{formatDateTime(entry.start)}</TableCell>
                              <TableCell>{entry.end ? formatDateTime(entry.end) : <Badge color="green" size="xs">En cours</Badge>}</TableCell>
                              <TableCell>
                                <Flex justifyContent="start" className="gap-1">
                                  <SpeakerWaveIcon className={`w-4 h-4 ${entry.output_muted ? 'text-red-500' : 'text-green-500'}`} />
                                  <Text>
                                    {entry.output_muted ? '🔇' : '🔊'} {entry.output_volume !== null ? `${entry.output_volume}%` : '—'}
                                  </Text>
                                </Flex>
                              </TableCell>
                              <TableCell>
                                <Text className="text-xs text-gray-500 break-all" title={entry.output_device || ''}>
                                  {entry.output_device || '—'}
                                </Text>
                              </TableCell>
                              <TableCell>
                                <Flex justifyContent="start" className="gap-1">
                                  <MicrophoneIcon className={`w-4 h-4 ${entry.input_muted ? 'text-red-500' : 'text-green-500'}`} />
                                  <Text>
                                    {entry.input_muted ? '🔇' : '🎤'} {entry.input_volume !== null ? `${entry.input_volume}%` : '—'}
                                  </Text>
                                </Flex>
                              </TableCell>
                              <TableCell>
                                <Text className="text-xs text-gray-500 break-all" title={entry.input_device || ''}>
                                  {entry.input_device || '—'}
                                </Text>
                              </TableCell>
                              <TableCell>
                                {entry.usb_audio_device ? (
                                  <Badge color={entry.audio_device_mismatch ? 'orange' : 'green'} size="xs">
                                    {entry.usb_audio_device}
                                  </Badge>
                                ) : (
                                  <Text className="text-xs text-gray-400">—</Text>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabPanel>

                  {/* Sub-tab: Audio Device Changes */}
                  <TabPanel>
                    <Flex justifyContent="end" className="mb-4">
                      {!isLoadingAudioDevices && (
                        <Badge color="purple">{audioDevicesHistory.length} changements</Badge>
                      )}
                    </Flex>

                    {isLoadingAudioDevices ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-fuchsia-500" />
                      </div>
                    ) : audioDevicesHistory.length === 0 ? (
                      <Text className="text-gray-500 text-center py-8">Aucun changement de périphérique détecté</Text>
                    ) : (
                      <div className="space-y-2">
                        {audioDevicesHistory.map((entry: AudioDeviceChange, index: number) => (
                          <div
                            key={index}
                            className={`p-3 rounded-lg border-l-4 ${
                              entry.audio_device_mismatch
                                ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                                : 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            }`}
                          >
                            <Flex justifyContent="between" alignItems="start">
                              <div className="flex-1 space-y-1">
                                <Flex justifyContent="start" className="gap-2">
                                  <Text className="text-xs text-gray-500 font-mono">{formatDateTime(entry.time)}</Text>
                                  {entry.backend && <Badge color="gray" size="xs">{entry.backend}</Badge>}
                                  {entry.audio_device_mismatch && (
                                    <Badge color="orange" size="xs" icon={ExclamationTriangleIcon}>Mismatch</Badge>
                                  )}
                                </Flex>
                                <Flex justifyContent="start" className="gap-4">
                                  <div>
                                    <Text className="text-xs text-gray-500">Entrée</Text>
                                    <Text className={`text-sm ${entry.input_changed ? 'font-semibold text-fuchsia-600' : ''}`}>
                                      {entry.input_changed && '→ '}{entry.input_device || '—'}
                                    </Text>
                                  </div>
                                  <div>
                                    <Text className="text-xs text-gray-500">Sortie</Text>
                                    <Text className={`text-sm ${entry.output_changed ? 'font-semibold text-violet-600' : ''}`}>
                                      {entry.output_changed && '→ '}{entry.output_device || '—'}
                                    </Text>
                                  </div>
                                  {entry.usb_audio_device && (
                                    <div>
                                      <Text className="text-xs text-gray-500">USB</Text>
                                      <Text className="text-sm">{entry.usb_audio_device}</Text>
                                    </div>
                                  )}
                                </Flex>
                              </div>
                            </Flex>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabPanel>
                </TabPanels>
              </TabGroup>
            </Card>
          </TabPanel>

          {/* Tab 4: Actions & Commands — only rendered when the role can write */}
          {canWrite && (
          <TabPanel>
            <div className="space-y-6 mt-6">
              {/* Volume Enforcement Config */}
              {operator && (
                <Card>
                  <Title className="mb-1">Contrôle du volume audio</Title>
                  <Text className="text-xs text-gray-500 mb-4">
                    Fourchette autorisée. L'agent corrige automatiquement si hors limites (toutes les 10s).
                  </Text>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <Text className="text-xs font-medium mb-2">Micro (min - max %)</Text>
                      <div className="flex gap-2 items-center">
                        <NumberInput
                          value={operator.mic_volume_min ?? undefined}
                          onValueChange={(v) => {
                            updateOperator(operator.id, { mic_volume_min: v ?? null })
                              .then(() => queryClient.invalidateQueries({ queryKey: ['operator-by-machine'] }))
                              .catch(() => alert('Erreur lors de la mise à jour du volume'))
                          }}
                          min={0} max={100} placeholder="Min"
                          className="w-24"
                        />
                        <Text className="text-gray-400">—</Text>
                        <NumberInput
                          value={operator.mic_volume_max ?? undefined}
                          onValueChange={(v) => {
                            updateOperator(operator.id, { mic_volume_max: v ?? null })
                              .then(() => queryClient.invalidateQueries({ queryKey: ['operator-by-machine'] }))
                              .catch(() => alert('Erreur lors de la mise à jour du volume'))
                          }}
                          min={0} max={100} placeholder="Max"
                          className="w-24"
                        />
                      </div>
                    </div>
                    <div>
                      <Text className="text-xs font-medium mb-2">Sortie casque (min - max %)</Text>
                      <div className="flex gap-2 items-center">
                        <NumberInput
                          value={operator.output_volume_min ?? undefined}
                          onValueChange={(v) => {
                            updateOperator(operator.id, { output_volume_min: v ?? null })
                              .then(() => queryClient.invalidateQueries({ queryKey: ['operator-by-machine'] }))
                              .catch(() => alert('Erreur lors de la mise à jour du volume'))
                          }}
                          min={0} max={100} placeholder="Min"
                          className="w-24"
                        />
                        <Text className="text-gray-400">—</Text>
                        <NumberInput
                          value={operator.output_volume_max ?? undefined}
                          onValueChange={(v) => {
                            updateOperator(operator.id, { output_volume_max: v ?? null })
                              .then(() => queryClient.invalidateQueries({ queryKey: ['operator-by-machine'] }))
                              .catch(() => alert('Erreur lors de la mise à jour du volume'))
                          }}
                          min={0} max={100} placeholder="Max"
                          className="w-24"
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Tailscale (Nafaq) */}
              {operator && (
                <Card>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <Title>Tailscale (Nafaq)</Title>
                      <Text className="text-xs text-gray-500">
                        Connexion VPN au serveur Headscale <code>nafaq.education-media.net</code>
                      </Text>
                    </div>
                    {operator.tailscale_ip ? (
                      <Badge color="green" icon={CheckCircleIcon}>
                        Connecté — {operator.tailscale_ip}
                      </Badge>
                    ) : (
                      <Badge color="gray">Non installé</Badge>
                    )}
                  </div>

                  {operator.tailscale_hostname && (
                    <Text className="text-xs text-gray-600 mb-2">
                      Hostname : <code>{operator.tailscale_hostname}</code>
                      {operator.tailscale_installed_at && (
                        <span className="text-gray-400">
                          {' '}· Installé le {formatDateTime(operator.tailscale_installed_at)}
                        </span>
                      )}
                    </Text>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2 mt-4">
                    <Button
                      icon={GlobeAltIcon}
                      onClick={() => {
                        setTailscaleHostname(operator.tailscale_hostname || '')
                        setTailscaleAuthkey('')
                        setShowTailscaleModal(true)
                      }}
                      disabled={!isOnline || installTailscaleMutation.isPending}
                      loading={installTailscaleMutation.isPending}
                      className="!min-h-[44px]"
                    >
                      {operator.tailscale_ip ? 'Réinstaller Tailscale' : 'Installer Tailscale'}
                    </Button>
                    {operator.tailscale_ip && (
                      <Button
                        icon={XCircleIcon}
                        variant="secondary"
                        color="red"
                        onClick={() => {
                          if (confirm('Déconnecter cet opérateur de Tailscale ?')) {
                            uninstallTailscaleMutation.mutate()
                          }
                        }}
                        disabled={!isOnline || uninstallTailscaleMutation.isPending}
                        className="!min-h-[44px]"
                      >
                        Déconnecter
                      </Button>
                    )}
                  </div>
                  {!isOnline && (
                    <Text className="text-yellow-600 mt-2 text-xs">
                      L'opérateur doit être en ligne pour modifier Tailscale
                    </Text>
                  )}
                </Card>
              )}

              {/* ActivityWatch */}
              {operator && (
                <Card>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <Title>Suivi d'activité (ActivityWatch)</Title>
                      <Text className="text-xs text-gray-500">
                        Suivi local par catégorie — seules les catégories quittent le poste, jamais les titres de fenêtre
                      </Text>
                    </div>
                    {machine?.metrics?.activity_afk != null ? (
                      <Badge color="green" icon={CheckCircleIcon}>Actif</Badge>
                    ) : (
                      <Badge color="gray">Aucune donnée</Badge>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 mt-4">
                    <Button
                      onClick={() => { setAwSha256(''); setShowActivityWatchModal(true) }}
                      disabled={!isOnline || commandMutation.isPending}
                      className="!min-h-[44px]"
                    >
                      {machine?.metrics?.activity_afk != null ? 'Réinstaller ActivityWatch' : 'Installer ActivityWatch'}
                    </Button>
                    <Button
                      variant="secondary"
                      color="red"
                      onClick={() => { setAwPurgeData(false); setShowActivityWatchUninstallModal(true) }}
                      disabled={!isOnline || commandMutation.isPending}
                      className="!min-h-[44px]"
                    >
                      Désinstaller
                    </Button>
                  </div>
                  {!isOnline && (
                    <Text className="text-yellow-600 mt-2 text-xs">
                      L'opérateur doit être en ligne pour modifier ActivityWatch
                    </Text>
                  )}
                </Card>
              )}

              {/* Actions Section */}
              <Card>
                <Title className="mb-4">Actions</Title>
                {commandMessage && (
                  <Callout
                    className="mb-4"
                    title={commandMessage.type === 'success' ? 'Succès' : 'Erreur'}
                    color={commandMessage.type === 'success' ? 'green' : 'red'}
                    icon={commandMessage.type === 'success' ? CheckCircleIcon : XCircleIcon}
                  >
                    {commandMessage.text}
                  </Callout>
                )}
                {/* Diagnostic */}
                <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Diagnostic</Text>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-5">
                  <Button
                    icon={SignalIcon}
                    variant="secondary"
                    onClick={() => commandMutation.mutate({ type: 'speedtest' })}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Test vitesse
                  </Button>
                  <Button
                    icon={DocumentTextIcon}
                    variant="secondary"
                    onClick={() => commandMutation.mutate({ type: 'get_logs', payload: { lines: 100 } })}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Voir logs
                  </Button>
                  <Button
                    icon={DocumentTextIcon}
                    variant="secondary"
                    color="indigo"
                    onClick={() => commandMutation.mutate({ type: 'deep_audio_diagnostic' })}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Diag audio profond
                  </Button>
                </div>

                {/* Audio / Linphone */}
                <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Audio / Linphone</Text>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-5">
                  <Button
                    icon={SpeakerWaveIcon}
                    variant="secondary"
                    color="blue"
                    onClick={() => {
                      if (confirm('Cela va reconfigurer Linphone sur le device audio par défaut et redémarrer Linphone. Continuer ?')) {
                        commandMutation.mutate({ type: 'fix_audio_config' })
                      }
                    }}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Fix audio config
                  </Button>
                  <Button
                    icon={MicrophoneIcon}
                    variant="secondary"
                    color="indigo"
                    onClick={() => {
                      if (confirm("Cela va configurer l'anti-écho au niveau OS (PipeWire/PulseAudio) et redémarrer le serveur audio. L'opératrice ne doit PAS être en appel. Continuer ?")) {
                        commandMutation.mutate({ type: 'setup_echo_cancel' })
                      }
                    }}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Setup anti-écho
                  </Button>
                  <Button
                    icon={SpeakerWaveIcon}
                    variant="secondary"
                    color="red"
                    onClick={() => {
                      const offlineNote = isOnline ? '' : "\n\n(Agent hors ligne : la commande sera mise en file d'attente et partira à sa prochaine connexion, validité 24h.)"
                      if (confirm("Re-forcer les périphériques EMCALL (anti-écho) comme sortie/micro par défaut et redémarrer Linphone ? À utiliser quand le device a dérivé. Nécessite que l'anti-écho ait déjà été installé. Pas pendant un appel." + offlineNote)) {
                        commandMutation.mutate({ type: 'force_emcall', queueIfOffline: true })
                      }
                    }}
                    disabled={commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Forcer EMCALL
                  </Button>
                  <Button
                    icon={SpeakerWaveIcon}
                    variant="secondary"
                    color="green"
                    onClick={() => {
                      if (confirm("Activer le codec Opus (HD, meilleur pour connexions faibles) et redémarrer Linphone ? Nécessite qu'Opus soit autorisé sur Asterisk, sinon retombe sur PCMU. Pas pendant un appel.")) {
                        commandMutation.mutate({ type: 'set_audio_codec', payload: { prefer_opus: true } })
                      }
                    }}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Activer Opus (HD)
                  </Button>
                  <Button
                    icon={WifiIcon}
                    variant="secondary"
                    color="cyan"
                    onClick={() => commandMutation.mutate({ type: 'wifi_report' })}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Rapport WiFi
                  </Button>
                  <Button
                    icon={ChatBubbleLeftRightIcon}
                    variant="secondary"
                    color="blue"
                    onClick={() => setShowMessageModal(true)}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Envoyer un message
                  </Button>
                  <Button
                    icon={PhoneIcon}
                    variant="secondary"
                    onClick={() => setShowLinphoneModal(true)}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Installer Linphone
                  </Button>
                  <Button
                    icon={TrashIcon}
                    variant="secondary"
                    onClick={() => commandMutation.mutate({ type: 'clean_linphone' })}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Nettoyer Linphone
                  </Button>
                  <Button
                    icon={KeyIcon}
                    variant="secondary"
                    onClick={() => setShowSipPasswordModal(true)}
                    className="!min-h-[44px] !justify-start"
                  >
                    Mot de passe SIP
                  </Button>
                </div>

                {/* Système */}
                <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Système</Text>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  <Button
                    icon={ArrowPathIcon}
                    onClick={() => commandMutation.mutate({ type: 'update_agent' })}
                    disabled={!isOnline || commandMutation.isPending}
                    loading={commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Mettre à jour agent
                  </Button>
                  <Button
                    icon={GlobeAltIcon}
                    variant="secondary"
                    onClick={() => commandMutation.mutate({ type: 'restart_network' })}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Redémarrer réseau
                  </Button>
                  <Button
                    icon={SignalIcon}
                    variant="secondary"
                    color="cyan"
                    onClick={() => {
                      if (confirm("Installer l'indicateur WiFi (signal en %) dans la barre du haut GNOME ? L'opératrice doit avoir une session graphique active.")) {
                        commandMutation.mutate({ type: 'install_wifi_indicator' })
                      }
                    }}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Installer indicateur WiFi
                  </Button>
                  <Button
                    icon={SignalIcon}
                    variant="secondary"
                    color="red"
                    onClick={() => {
                      if (confirm("Désinstaller l'indicateur WiFi ?")) {
                        commandMutation.mutate({ type: 'uninstall_wifi_indicator' })
                      }
                    }}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Désinstaller indicateur WiFi
                  </Button>
                  <Button
                    icon={WifiIcon}
                    variant="secondary"
                    color="amber"
                    onClick={() => {
                      if (confirm("Désactiver le WiFi powersave (NetworkManager) ? Cela réduit les coupures WiFi mais provoque une brève coupure (~3-5s) lors du redémarrage NM. À ne pas faire pendant un appel.")) {
                        commandMutation.mutate({ type: 'disable_wifi_powersave' })
                      }
                    }}
                    disabled={!isOnline || commandMutation.isPending}
                    className="!min-h-[44px] !justify-start"
                  >
                    Désactiver WiFi powersave
                  </Button>
                </div>
                {!isOnline && (
                  <Text className="text-yellow-600 mt-2 text-sm">
                    L'opérateur doit être en ligne pour exécuter des actions
                  </Text>
                )}
              </Card>

              {/* Diagnostics */}
              <Card>
                <Flex justifyContent="between" className="mb-4">
                  <Title>Diagnostics Système</Title>
                  <Button
                    size="xs"
                    variant="secondary"
                    icon={DocumentTextIcon}
                    onClick={() => {
                      if (!machineId) return
                      exportDiagnostics(machineId, 7).catch((err) => {
                        console.error('Export des diagnostics échoué', err)
                      })
                    }}
                  >
                    Exporter (7j)
                  </Button>
                </Flex>
                <Text className="text-xs text-gray-500">
                  Collecte automatique 3x/jour (8h, 13h, 18h). Inclut système, réseau, audio, Linphone, agent.
                </Text>
              </Card>

              {/* Deep Audio Diagnostics */}
              <Card>
                <Flex justifyContent="between" alignItems="center" className="mb-3">
                  <div>
                    <Title>Diagnostics Audio Profonds</Title>
                    <Text className="text-xs text-gray-500">
                      Captures manuelles complètes (pactl, pw-dump, configs, hardware, linphonerc, ping SIP).
                      Utiliser pour comparer deux opératrices.
                    </Text>
                  </div>
                  <Badge color="indigo">{deepAudioDiagnostics?.length ?? 0}</Badge>
                </Flex>
                {!deepAudioDiagnostics || deepAudioDiagnostics.length === 0 ? (
                  <Text className="text-gray-500 text-sm text-center py-6">
                    Aucun diagnostic audio profond. Clique sur « Diagnostic Audio Profond » dans les actions ci-dessus.
                  </Text>
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Date</TableHeaderCell>
                        <TableHeaderCell>ID</TableHeaderCell>
                        <TableHeaderCell className="text-right">Action</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {deepAudioDiagnostics.map((d) => (
                        <TableRow key={d.id}>
                          <TableCell>
                            <Text className="text-sm">{formatDateTime(d.collected_at)}</Text>
                          </TableCell>
                          <TableCell>
                            <Text className="text-xs text-gray-500">#{d.id}</Text>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="xs"
                              variant="secondary"
                              icon={DocumentTextIcon}
                              onClick={() => {
                                downloadDiagnosticFile(d.id).catch((err) => {
                                  const detail = err?.response?.data?.detail || err?.message || 'Échec du téléchargement'
                                  setCommandMessage({ type: 'error', text: detail })
                                  setTimeout(() => setCommandMessage(null), 5000)
                                })
                              }}
                            >
                              Télécharger JSON
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>

              {/* Command History */}
              <Card>
                <Title className="mb-4">Historique des Commandes</Title>
                {!commandsData?.commands || commandsData.commands.length === 0 ? (
                  <Text className="text-gray-500 text-center py-8">Aucune commande exécutée</Text>
                ) : (
                  <div className="overflow-x-auto">
                  <Table className="table-fixed [&>div]:!overflow-hidden min-w-[640px]">
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell className="w-24">Statut</TableHeaderCell>
                        <TableHeaderCell className="w-32">Commande</TableHeaderCell>
                        <TableHeaderCell className="w-32">Date</TableHeaderCell>
                        <TableHeaderCell>Résultat</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {commandsData.commands.map((cmd: Command) => (
                        <TableRow key={cmd.id}>
                          <TableCell>
                            <Badge
                              color={cmd.status === 'completed' ? 'green' : cmd.status === 'failed' ? 'red' : 'yellow'}
                              icon={cmd.status === 'completed' ? CheckCircleIcon : cmd.status === 'failed' ? XCircleIcon : ClockIcon}
                            >
                              {cmd.status === 'completed' ? 'Succès' : cmd.status === 'failed' ? 'Échec' : 'En cours'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge color={
                              cmd.command_type === 'speedtest' ? 'blue' :
                              cmd.command_type === 'update_agent' ? 'purple' :
                              cmd.command_type === 'restart_network' ? 'orange' :
                              cmd.command_type === 'get_logs' ? 'gray' :
                              cmd.command_type === 'install_linphone' ? 'green' :
                              cmd.command_type === 'install_tailscale' ? 'indigo' :
                              cmd.command_type === 'uninstall_tailscale' ? 'red' :
                              cmd.command_type === 'setup_echo_cancel' ? 'indigo' :
                              cmd.command_type === 'deep_audio_diagnostic' ? 'indigo' :
                              cmd.command_type === 'install_wifi_indicator' ? 'cyan' :
                              cmd.command_type === 'uninstall_wifi_indicator' ? 'red' :
                              cmd.command_type === 'disable_wifi_powersave' ? 'amber' :
                              cmd.command_type === 'set_audio_codec' ? 'green' :
                              cmd.command_type === 'force_emcall' ? 'red' :
                              cmd.command_type === 'notify_operator' ? (parseCmdParams(cmd)?.source === 'alert' ? 'orange' : 'blue') :
                              cmd.command_type === 'wifi_report' ? 'cyan' :
                              cmd.command_type === 'clean_linphone' ? 'yellow' : 'gray'
                            }>
                              {cmd.command_type === 'speedtest' ? 'Test vitesse' :
                               cmd.command_type === 'update_agent' ? 'Mise à jour' :
                               cmd.command_type === 'restart_network' ? 'Redémarrage réseau' :
                               cmd.command_type === 'get_logs' ? 'Logs' :
                               cmd.command_type === 'install_linphone' ? 'Install Linphone' :
                               cmd.command_type === 'install_tailscale' ? 'Install Tailscale' :
                               cmd.command_type === 'uninstall_tailscale' ? 'Uninstall Tailscale' :
                               cmd.command_type === 'setup_echo_cancel' ? 'Setup Anti-Écho' :
                               cmd.command_type === 'deep_audio_diagnostic' ? 'Diag Audio Profond' :
                               cmd.command_type === 'install_wifi_indicator' ? 'Install WiFi Indicateur' :
                               cmd.command_type === 'uninstall_wifi_indicator' ? 'Uninstall WiFi Indicateur' :
                               cmd.command_type === 'disable_wifi_powersave' ? 'Disable WiFi Powersave' :
                               cmd.command_type === 'set_audio_codec' ? 'Codec Opus' :
                               cmd.command_type === 'force_emcall' ? 'Forcer EMCALL' :
                               cmd.command_type === 'notify_operator' ? (parseCmdParams(cmd)?.source === 'alert' ? 'Alerte PC' : 'Message') :
                               cmd.command_type === 'wifi_report' ? 'Rapport WiFi' :
                               cmd.command_type === 'clean_linphone' ? 'Clean Linphone' : cmd.command_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Text className="text-sm">{formatDateTime(cmd.created_at)}</Text>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              let result = cmd.result
                              if (typeof result === 'string') {
                                try {
                                  result = JSON.parse(result)
                                } catch {
                                  // Keep as string
                                }
                              }
                              const cmdParams = parseCmdParams(cmd)

                              if (cmd.status === 'pending' || cmd.status === 'sent') {
                                return <Text className="text-gray-500 text-sm italic">En attente...</Text>
                              }
                              if (cmd.error) {
                                return <p className="text-red-500 text-sm break-words whitespace-normal">{cmd.error}</p>
                              }
                              if (cmd.command_type === 'speedtest' && result && typeof result === 'object') {
                                const wrapper = result as Record<string, unknown>
                                const speedData = (wrapper.result as Record<string, unknown>) || wrapper
                                const download = speedData.download_mbps ?? speedData.download
                                const upload = speedData.upload_mbps ?? speedData.upload
                                const ping = speedData.ping_ms ?? speedData.ping

                                if (download != null || upload != null || ping != null) {
                                  return (
                                    <div className="space-y-0.5">
                                      <Text className="text-sm">
                                        <span className="text-green-600 dark:text-green-400 font-medium">↓ {download != null ? Number(download).toFixed(1) : '-'}</span> Mbps
                                      </Text>
                                      <Text className="text-sm">
                                        <span className="text-blue-600 dark:text-blue-400 font-medium">↑ {upload != null ? Number(upload).toFixed(1) : '-'}</span> Mbps
                                      </Text>
                                      <Text className="text-sm">
                                        <span className="text-orange-600 dark:text-orange-400 font-medium">{ping != null ? Number(ping).toFixed(0) : '-'}</span> ms
                                      </Text>
                                    </div>
                                  )
                                }
                              }
                              if (cmd.command_type === 'notify_operator') {
                                const msg = cmdParams?.message as string | undefined
                                const sev = cmdParams?.severity as string | undefined
                                return (
                                  <div className="space-y-0.5">
                                    {sev && (
                                      <Badge color={sev === 'critical' ? 'red' : sev === 'high' ? 'orange' : sev === 'low' ? 'gray' : 'yellow'} size="xs">
                                        {sev}
                                      </Badge>
                                    )}
                                    <p className="text-xs text-gray-600 dark:text-gray-300 break-words whitespace-pre-wrap line-clamp-4">
                                      {msg || '—'}
                                    </p>
                                    {cmd.status === 'failed' && cmd.error && (
                                      <p className="text-xs text-red-500">{cmd.error}</p>
                                    )}
                                  </div>
                                )
                              }
                              if (cmd.command_type === 'wifi_report' && result && typeof result === 'object') {
                                const rep = (result as Record<string, unknown>).report as Record<string, unknown> | undefined
                                if (rep) {
                                  const interference = rep.interference as Record<string, unknown> | undefined
                                  const risk = interference?.risk as string | undefined
                                  return (
                                    <div className="space-y-1">
                                      <Badge color={risk === 'élevé' ? 'red' : risk === 'moyen' ? 'amber' : 'green'}>
                                        Interférence: {risk ?? '?'}
                                      </Badge>
                                      <div>
                                        <Button size="xs" variant="light" icon={WifiIcon} onClick={() => setWifiReport(rep)}>
                                          Voir le rapport
                                        </Button>
                                      </div>
                                    </div>
                                  )
                                }
                              }
                              if (result) {
                                const wrapper = result as Record<string, unknown>
                                if (wrapper.error && typeof wrapper.error === 'string') {
                                  return <p className="text-red-500 text-sm break-words whitespace-normal">{wrapper.error}</p>
                                }
                                return (
                                  <Accordion>
                                    <AccordionHeader>
                                      <Text className="text-sm text-blue-500">Voir détails</Text>
                                    </AccordionHeader>
                                    <AccordionBody>
                                      <pre className="bg-gray-100 dark:bg-gray-800 p-2 rounded text-xs overflow-auto max-h-40 whitespace-pre-wrap break-words">
                                        {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
                                      </pre>
                                    </AccordionBody>
                                  </Accordion>
                                )
                              }
                              return <Text className="text-gray-500 text-sm">—</Text>
                            })()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                )}
              </Card>
            </div>
          </TabPanel>
          )}

          {/* Tab 5: Activité — only rendered when the role can view it (admin/manager) */}
          {canViewActivity && (
          <TabPanel>
            <div className="space-y-6 mt-6">
              {/* Real-time status banner */}
              <Card>
                {machine?.metrics?.activity_afk == null ? (
                  <Flex justifyContent="start" className="gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5 text-gray-400" />
                    <Text className="text-gray-500">Données d'activité non disponibles</Text>
                  </Flex>
                ) : (
                  <Flex justifyContent="start" className="gap-2">
                    <Badge color={machine.metrics.activity_afk ? 'gray' : 'green'}>
                      {realtimeActivityLabel(machine.metrics.activity_afk, machine.metrics.activity_category)}
                    </Badge>
                  </Flex>
                )}
              </Card>

              {/* Period selector */}
              <div className="flex justify-end">
                <Select value={activityPeriod} onValueChange={(v) => setActivityPeriod(v as ActivityPeriod)} className="w-full sm:w-32">
                  <SelectItem value="24h">24 heures</SelectItem>
                  <SelectItem value="7d">7 jours</SelectItem>
                  <SelectItem value="30d">30 jours</SelectItem>
                </Select>
              </div>

              {/* Category breakdown */}
              <Card>
                <Flex justifyContent="between" className="mb-4">
                  <Title>Répartition par catégorie</Title>
                  {activitySummary && (
                    <Badge color="blue">Couverture de la mesure : {activitySummary.coverage_pct}%</Badge>
                  )}
                </Flex>
                {isLoadingActivitySummary ? (
                  <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                  </div>
                ) : !activitySummary || activitySummary.categories.length === 0 ? (
                  <Text className="text-gray-500 text-center py-8">Aucune donnée d'activité disponible</Text>
                ) : (
                  <DonutChart
                    className="h-64"
                    data={activitySummary.categories.map((c) => ({
                      name: activityCategoryLabel(c.category),
                      seconds: c.seconds,
                    }))}
                    category="seconds"
                    index="name"
                    valueFormatter={(v: number) => `${Math.round(v / 60)} min`}
                    colors={ACTIVITY_CATEGORY_COLORS}
                  />
                )}
              </Card>

              {/* Hourly timeline */}
              <Card>
                <Title className="mb-4">Chronologie horaire</Title>
                {isLoadingActivityTimeline ? (
                  <div className="flex items-center justify-center h-72">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                  </div>
                ) : activityTimelineError ? (
                  <Text className="text-gray-500 text-center py-8">
                    Impossible de charger la chronologie pour cette période
                  </Text>
                ) : !activityTimeline || activityTimeline.points.length === 0 ? (
                  <Text className="text-gray-500 text-center py-8">Aucune donnée de chronologie disponible</Text>
                ) : (
                  <BarChart
                    className="h-72"
                    data={activityTimeline.points.map((p) => {
                      const row: Record<string, string | number> = {
                        time: formatDateTime(p.t),
                      }
                      for (const [category, seconds] of Object.entries(p.durations)) {
                        row[activityCategoryLabel(category)] = Math.round(seconds / 60)
                      }
                      return row
                    })}
                    index="time"
                    categories={activityTimelineCategories}
                    colors={ACTIVITY_CATEGORY_COLORS}
                    stack
                    valueFormatter={(v: number) => `${v} min`}
                    showLegend
                  />
                )}
              </Card>

              {/* Focus / VoIP degradation correlation */}
              <Card>
                <Title className="mb-4">Corrélation focus / dégradation VoIP (7 derniers jours)</Title>
                {isLoadingFocusCorrelation ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
                  </div>
                ) : !focusCorrelation || focusCorrelation.windows.length === 0 ? (
                  <Text className="text-gray-500 text-center py-8">Aucune fenêtre de dégradation détectée</Text>
                ) : (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>Fenêtre</TableHeaderCell>
                        <TableHeaderCell>Focus VoIP</TableHeaderCell>
                        <TableHeaderCell>Navigateur — autre</TableHeaderCell>
                        <TableHeaderCell>AFK</TableHeaderCell>
                        <TableHeaderCell>En appel</TableHeaderCell>
                        <TableHeaderCell>Verdict</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {focusCorrelation.windows.map((w, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {formatDateTime(w.window_start)} → {formatDateTime(w.window_end)}
                          </TableCell>
                          <TableCell>{w.voip_focus_pct}%</TableCell>
                          <TableCell>{w.browser_other_pct}%</TableCell>
                          <TableCell>{w.afk_pct}%</TableCell>
                          <TableCell>
                            <Badge color={w.in_call ? 'green' : 'gray'}>{w.in_call ? 'Oui' : 'Non'}</Badge>
                          </TableCell>
                          <TableCell>{w.verdict}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Card>
            </div>
          </TabPanel>
          )}
        </TabPanels>
      </TabGroup>

      {/* Audio Config Modal */}
      {operator && (
        <AudioConfigModal
          isOpen={showAudioConfig}
          onClose={() => setShowAudioConfig(false)}
          operatorId={operator.id}
          operatorName={operator.fullname}
          currentInputDevice={metrics?.input_device || null}
          currentOutputDevice={metrics?.output_device || null}
          expectedInputDevice={operator.expected_input_device}
          expectedOutputDevice={operator.expected_output_device}
          usbAudioDevice={metrics?.usb_audio_device || null}
        />
      )}

      {/* Linphone Install Modal */}
      {showLinphoneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-lg sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-4">Installer Linphone</Title>
            <div className="space-y-4">
              <div>
                <Text className="mb-1 font-medium">Extension SIP</Text>
                <input
                  type="text"
                  value={extension}
                  disabled
                  className="w-full px-3 py-2 border rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500"
                />
              </div>
              <div>
                <Text className="mb-1 font-medium">Mot de passe SIP</Text>
                <input
                  type="password"
                  value={linphonePassword}
                  onChange={(e) => setLinphonePassword(e.target.value)}
                  placeholder="Mot de passe du compte SIP"
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                  autoFocus
                />
                <Text className="text-xs text-gray-500 mt-1">
                  Le mot de passe sera sauvegardé pour les futures installations
                </Text>
              </div>
              <div>
                <Text className="mb-1 font-medium">Serveur SIP</Text>
                <select
                  value={sipServer}
                  onChange={(e) => setSipServer(e.target.value as 'emcall.education-media.net' | 'emcall.emc')}
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                >
                  <option value="emcall.education-media.net">emcall.education-media.net (Internet)</option>
                  <option value="emcall.emc">emcall.emc (VPN Tailscale)</option>
                </select>
                <Text className="text-xs text-gray-500 mt-1">
                  Utiliser emcall.emc uniquement si Tailscale est installé et connecté
                </Text>
              </div>
              <Flex className="gap-3 justify-end mt-6">
                <Button
                  variant="secondary"
                  onClick={() => { setShowLinphoneModal(false); setLinphonePassword('') }}
                >
                  Annuler
                </Button>
                <Button
                  variant="secondary"
                  disabled={!linphonePassword || sipPasswordMutation.isPending}
                  loading={sipPasswordMutation.isPending}
                  onClick={() => sipPasswordMutation.mutate(linphonePassword)}
                >
                  Sauvegarder MDP uniquement
                </Button>
                <Button
                  icon={PhoneIcon}
                  disabled={!linphonePassword || commandMutation.isPending}
                  loading={commandMutation.isPending}
                  onClick={() => {
                    sipPasswordMutation.mutate(linphonePassword)
                    commandMutation.mutate({
                      type: 'install_linphone',
                      payload: { extension, password: linphonePassword, sip_server: sipServer }
                    })
                  }}
                >
                  Sauvegarder + Installer
                </Button>
              </Flex>
            </div>
          </Card>
        </div>
      )}
      {/* SIP Password Modal */}
      {showSipPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-md sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-4">Mot de passe SIP</Title>
            <div className="space-y-4">
              <div>
                <Text className="mb-1 font-medium">Extension</Text>
                <input
                  type="text"
                  value={extension}
                  disabled
                  className="w-full px-3 py-2 border rounded-md bg-gray-100 dark:bg-gray-800 text-gray-500"
                />
              </div>
              <div>
                <Text className="mb-1 font-medium">Nouveau mot de passe SIP</Text>
                <input
                  type="password"
                  value={sipPassword}
                  onChange={(e) => setSipPassword(e.target.value)}
                  placeholder="Entrer le mot de passe SIP"
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                  autoFocus
                />
              </div>
              <Flex className="gap-3 justify-end mt-6">
                <Button
                  variant="secondary"
                  onClick={() => { setShowSipPasswordModal(false); setSipPassword('') }}
                >
                  Annuler
                </Button>
                <Button
                  icon={KeyIcon}
                  disabled={!sipPassword || sipPasswordMutation.isPending}
                  loading={sipPasswordMutation.isPending}
                  onClick={() => sipPasswordMutation.mutate(sipPassword)}
                >
                  Enregistrer
                </Button>
              </Flex>
            </div>
          </Card>
        </div>
      )}

      {/* Operator message modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-md sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-2">Envoyer un message à l'opératrice</Title>
            <Text className="text-xs text-gray-500 mb-4">
              S'affiche sur l'écran de l'opératrice (notification, ou fenêtre bloquante si « Critique »).
              Nécessite qu'elle soit en ligne.
            </Text>
            <div className="space-y-4">
              <div>
                <Text className="mb-1 font-medium">Message</Text>
                <textarea
                  value={operatorMessage}
                  onChange={(e) => setOperatorMessage(e.target.value)}
                  placeholder="Ex: Merci de vérifier votre casque avant le prochain appel."
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                  autoFocus
                />
              </div>
              <div>
                <Text className="mb-1 font-medium">Importance</Text>
                <select
                  value={messageSeverity}
                  onChange={(e) => setMessageSeverity(e.target.value as 'low' | 'medium' | 'high' | 'critical')}
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                >
                  <option value="low">Faible (notification discrète)</option>
                  <option value="medium">Moyenne (notification)</option>
                  <option value="high">Élevée (notification urgente)</option>
                  <option value="critical">Critique (fenêtre bloquante)</option>
                </select>
              </div>
              <Flex className="gap-3 justify-end mt-6">
                <Button
                  variant="secondary"
                  onClick={() => { setShowMessageModal(false); setOperatorMessage('') }}
                >
                  Annuler
                </Button>
                <Button
                  icon={ChatBubbleLeftRightIcon}
                  disabled={!operatorMessage.trim() || commandMutation.isPending}
                  loading={commandMutation.isPending}
                  onClick={() => commandMutation.mutate({
                    type: 'notify_operator',
                    payload: { message: operatorMessage.trim(), title: 'Message de votre superviseur', severity: messageSeverity },
                  })}
                >
                  Envoyer
                </Button>
              </Flex>
            </div>
          </Card>
        </div>
      )}

      {/* WiFi report modal */}
      {wifiReport && (() => {
        const r = wifiReport
        const itf = (r.interference || {}) as Record<string, unknown>
        const neighbors = (r.neighbors || []) as Array<Record<string, unknown>>
        const risk = itf.risk as string | undefined
        const row = (label: string, value: unknown) => (
          <div className="flex justify-between gap-4 py-0.5">
            <Text className="text-xs text-gray-500">{label}</Text>
            <Text className="text-xs font-medium text-right break-all">{value != null && value !== '' ? String(value) : '—'}</Text>
          </div>
        )
        const verdicts = (r.verdicts || []) as string[]
        const kernel = (r.kernel || {}) as Record<string, unknown>
        const bt = (r.bluetooth || {}) as Record<string, unknown>
        const dmesg = (r.dmesg || {}) as Record<string, unknown>
        const nmEvents = (r.nm_events || []) as string[]
        const station = (r.station || {}) as Record<string, unknown>
        return (
          <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
            <Card className="w-full sm:max-w-2xl sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
              <Flex justifyContent="between" className="mb-3">
                <Title>Rapport WiFi</Title>
                <Button size="xs" variant="light" onClick={() => setWifiReport(null)}>Fermer</Button>
              </Flex>

              {verdicts.length > 0 && (
                <div className="mb-4 rounded-md border border-blue-500/30 bg-blue-500/5 p-3">
                  <Text className="font-semibold mb-1">Diagnostic</Text>
                  <ul className="list-disc pl-4 space-y-1">
                    {verdicts.map((v, i) => (
                      <li key={i} className="text-xs">{v}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Text className="font-semibold mb-1">Carte & pilote</Text>
                  {row('Interface', r.interface)}
                  {row('Adaptateur', r.adapter)}
                  {row('Pilote', r.driver)}
                  {row('Version pilote', r.driver_version)}
                  {row('Firmware', r.firmware)}
                  {row('Bus', r.bus_info)}
                </div>
                <div>
                  <Text className="font-semibold mb-1">Connexion actuelle</Text>
                  {row('SSID', r.ssid)}
                  {row('BSSID', r.bssid)}
                  {row('Signal', r.signal_dbm != null ? `${r.signal_dbm} dBm` : null)}
                  {row('Canal', r.channel)}
                  {row('Bande', r.band)}
                  {row('Largeur', r.channel_width)}
                  {row('Débit TX/RX', `${r.tx_bitrate_mbps ?? '—'} / ${r.rx_bitrate_mbps ?? '—'} Mbps`)}
                  {row('Puissance TX', r.tx_power_dbm != null ? `${r.tx_power_dbm} dBm` : null)}
                </div>
                <div>
                  <Text className="font-semibold mb-1">Énergie & réglementation</Text>
                  {row('Power save (noyau)', r.power_save)}
                  {row('Power save (NM)', r.nm_powersave)}
                  {row('Pays (reg)', r.regulatory_country)}
                </div>
                <div>
                  <Text className="font-semibold mb-1">Interférence</Text>
                  <Badge color={risk === 'élevé' ? 'red' : risk === 'moyen' ? 'amber' : 'green'} className="mb-1">
                    Risque: {risk ?? '?'}
                  </Badge>
                  {row('APs même canal', itf.co_channel_aps)}
                  {row('APs chevauchants', itf.overlapping_aps)}
                  {row('Pression canal', itf.channel_pressure)}
                  {itf.recommended_channel_24ghz != null && row('Canal conseillé (2.4G)', itf.recommended_channel_24ghz)}
                </div>
                <div>
                  <Text className="font-semibold mb-1">Kernel</Text>
                  {row('Version actuelle', kernel.running)}
                  {row('Ubuntu', kernel.ubuntu_version)}
                  {row('HWE installé', kernel.hwe_installed || 'non')}
                  {row('HWE disponible', kernel.hwe_candidate || 'non')}
                  {kernel.hwe_upgrade_available === true && (
                    <Text className="text-xs text-red-500 mt-1">
                      ⚠ Upgrade kernel possible — À PLANIFIER SUR SITE, jamais à distance (risque de coupure réseau)
                    </Text>
                  )}
                </div>
                <div>
                  <Text className="font-semibold mb-1">Bluetooth & lien</Text>
                  {row('Bluetooth', bt.present ? (bt.active ? 'Actif' : 'Coupé') : 'Absent')}
                  {bt.coexistence_risk === true && (
                    <Text className="text-xs text-amber-500">⚠ Coexistence 2.4 GHz possible</Text>
                  )}
                  {row('TX retries', station.tx_retries)}
                  {row('TX failed', station.tx_failed)}
                  {row('RX drop', station.rx_drop_misc)}
                  {row('Signal moyen', station.signal_avg_dbm != null ? `${station.signal_avg_dbm} dBm` : null)}
                </div>
              </div>

              {((dmesg.lines as string[] | undefined)?.length ?? 0) > 0 && (
                <>
                  <Text className="font-semibold mt-4 mb-1">Erreurs noyau (dmesg)</Text>
                  <pre className="text-[10px] bg-gray-100 dark:bg-gray-800 rounded p-2 overflow-x-auto max-h-32 overflow-y-auto whitespace-pre-wrap break-all">
                    {(dmesg.lines as string[]).join('\n')}
                  </pre>
                </>
              )}
              {nmEvents.length > 0 && (
                <>
                  <Text className="font-semibold mt-4 mb-1">Événements NetworkManager</Text>
                  <pre className="text-[10px] bg-gray-100 dark:bg-gray-800 rounded p-2 overflow-x-auto max-h-32 overflow-y-auto whitespace-pre-wrap break-all">
                    {nmEvents.join('\n')}
                  </pre>
                </>
              )}

              <Text className="font-semibold mt-4 mb-1">Réseaux voisins ({neighbors.length})</Text>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-gray-500 text-left">
                      <th className="pr-2 py-1">SSID</th>
                      <th className="pr-2 py-1">Canal</th>
                      <th className="pr-2 py-1">Signal</th>
                      <th className="pr-2 py-1">Sécurité</th>
                    </tr>
                  </thead>
                  <tbody>
                    {neighbors.map((n, i) => (
                      <tr key={i} className={n.chan === r.channel ? 'text-red-500' : ''}>
                        <td className="pr-2 py-0.5 break-all">{String(n.ssid ?? '(caché)')}</td>
                        <td className="pr-2 py-0.5">{String(n.chan ?? '—')}</td>
                        <td className="pr-2 py-0.5">{n.signal != null ? `${n.signal}%` : '—'}</td>
                        <td className="pr-2 py-0.5">{String(n.security ?? '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )
      })()}

      {/* Tailscale Install Modal */}
      {showTailscaleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-lg sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-2">Installer Tailscale (Nafaq)</Title>
            <Text className="text-xs text-gray-500 mb-4">
              Connecte cet opérateur au serveur Headscale <code>nafaq.education-media.net</code>.
              L'installation peut prendre jusqu'à 5 minutes.
            </Text>
            <div className="space-y-4">
              <div>
                <Text className="mb-1 font-medium">Hostname Tailscale</Text>
                <input
                  type="text"
                  value={tailscaleHostname}
                  onChange={(e) => setTailscaleHostname(e.target.value)}
                  placeholder="ex: operator-100001"
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                  autoFocus
                />
                <Text className="text-xs text-gray-500 mt-1">
                  Doit correspondre au hostname configuré côté serveur Nafaq
                </Text>
              </div>
              <div>
                <Text className="mb-1 font-medium">Pre-auth key Headscale</Text>
                <input
                  type="password"
                  value={tailscaleAuthkey}
                  onChange={(e) => setTailscaleAuthkey(e.target.value)}
                  placeholder="tskey-auth-..."
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                />
                <Text className="text-xs text-gray-500 mt-1">
                  Clé pré-autorisée générée dans Nafaq (expire après 1h)
                </Text>
              </div>
              <Flex className="gap-3 justify-end mt-6">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowTailscaleModal(false)
                    setTailscaleAuthkey('')
                    setTailscaleHostname('')
                  }}
                >
                  Annuler
                </Button>
                <Button
                  icon={GlobeAltIcon}
                  disabled={
                    !tailscaleAuthkey ||
                    !tailscaleHostname ||
                    installTailscaleMutation.isPending
                  }
                  loading={installTailscaleMutation.isPending}
                  onClick={() =>
                    installTailscaleMutation.mutate({
                      authkey: tailscaleAuthkey,
                      hostname: tailscaleHostname,
                    })
                  }
                >
                  Installer
                </Button>
              </Flex>
            </div>
          </Card>
        </div>
      )}

      {/* ActivityWatch Install Modal */}
      {showActivityWatchModal && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-lg sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-2">Installer ActivityWatch</Title>
            <Text className="text-xs text-gray-500 mb-4">
              Suivi local d'activité par catégorie (127.0.0.1 seulement). Assure-toi que
              l'opérateur a donné son consentement avant d'activer le suivi.
            </Text>
            <div className="space-y-4">
              <div>
                <Text className="mb-1 font-medium">Version</Text>
                <input
                  type="text"
                  value={awVersion}
                  onChange={(e) => setAwVersion(e.target.value)}
                  placeholder="v0.13.2"
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700"
                  autoFocus
                />
              </div>
              <div>
                <Text className="mb-1 font-medium">SHA-256 de l'archive</Text>
                <input
                  type="text"
                  value={awSha256}
                  onChange={(e) => setAwSha256(e.target.value)}
                  placeholder="64 caractères hexadécimaux"
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-800 dark:border-gray-700 font-mono text-xs"
                />
                <Text className="text-xs text-gray-500 mt-1">
                  Calculé sur le serveur avec <code>sha256sum</code> lors du dépôt de l'archive
                  dans <code>server/api/vendor/</code> — voir <code>server/api/vendor/README.md</code>
                </Text>
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={awWithExtension}
                  onChange={(e) => setAwWithExtension(e.target.checked)}
                />
                <Text>Inclure l'extension navigateur (suivi des onglets)</Text>
              </label>
              <Flex className="gap-3 justify-end mt-6">
                <Button
                  variant="secondary"
                  onClick={() => { setShowActivityWatchModal(false); setAwSha256('') }}
                >
                  Annuler
                </Button>
                <Button
                  disabled={
                    !/^v\d+\.\d+\.\d+$/.test(awVersion.trim()) ||
                    !/^[a-fA-F0-9]{64}$/.test(awSha256.trim()) ||
                    commandMutation.isPending
                  }
                  loading={commandMutation.isPending}
                  onClick={() =>
                    commandMutation.mutate({
                      type: 'install_activitywatch',
                      payload: {
                        version: awVersion.trim(),
                        expected_sha256: awSha256.trim(),
                        with_browser_extension: awWithExtension,
                      },
                    })
                  }
                >
                  Installer
                </Button>
              </Flex>
            </div>
          </Card>
        </div>
      )}

      {/* ActivityWatch Uninstall Modal */}
      {showActivityWatchUninstallModal && (
        <div className="fixed inset-0 bg-black/50 flex items-stretch sm:items-center justify-center z-50">
          <Card className="w-full sm:max-w-lg sm:mx-4 sm:rounded-xl rounded-none sm:max-h-[90vh] max-h-screen overflow-y-auto">
            <Title className="mb-2">Désinstaller ActivityWatch</Title>
            <Text className="text-xs text-gray-500 mb-4">
              Retire la pile ActivityWatch de ce poste. Les données déjà remontées au serveur ne
              sont pas concernées par cette action.
            </Text>
            <div className="space-y-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={awPurgeData}
                  onChange={(e) => setAwPurgeData(e.target.checked)}
                />
                <Text>
                  Purger aussi les données locales d'activité de l'opérateur (irréversible)
                </Text>
              </label>
              <Flex className="gap-3 justify-end mt-6">
                <Button
                  variant="secondary"
                  onClick={() => setShowActivityWatchUninstallModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  color="red"
                  disabled={commandMutation.isPending}
                  loading={commandMutation.isPending}
                  onClick={() =>
                    commandMutation.mutate({
                      type: 'uninstall_activitywatch',
                      payload: { purge_data: awPurgeData, remove_extension_policy: true },
                    })
                  }
                >
                  Désinstaller
                </Button>
              </Flex>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
